import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// LevelEditorPage.tsx — the hand-authoring grid editor. Author a board (boxes, mystery, arrows, tunnels,
// gates, locks and links; group/sibling/sortOrder per cell), tune generation params, then Compile (colours
// are seed-generated on the backend) and Play the result in the game via the Unity bridge.
import { Component } from "react";
import { GridCanvas } from "../editor/GridCanvas";
import { MechanicIcon } from "../editor/MechanicIcon";
import { NumberInput, SelectOnClickGuard } from "../editor/NumberInput";
import { CellContextMenu } from "../editor/CellContextMenu";
import { ColorStudio, COLOR_NAMES, PAINTABLE_COLORS } from "../editor/ColorStudio";
import { paintedCellColor, withCellColor, withElevatorSpawnColor, withPipeSpawnColor, withInnerColor, withSliceColor, withSliceFrozen, withSplitColumnColor, withoutFrozenSlices, withSuperTunnelSpawnColor, withToolBoxSpawnColor, withTunnelSpawnColor } from "../editor/colorOps";
import { createEmptyTemplate, findCell, onlyDifficultyDiffers, templateStats, withBoxFlag, withCellAsPlainBox, withCellAsSplitBox, withSplit, splitPatch, withCellAsDoubleBox, withDouble, doublePatch, withCellAsJumpingJack, withJumpingJack, jackPatch, splitColumnPaint, SPLIT_COLUMNS, withCellAsTunnel, withCellAsSuperTunnel, withCellAsToolBox, withCellAsPipe, withCellAsPropeller, withPropellerDirection, withPropellerBox, withSpawnerCount, withSpawnerSpawn, toolBoxSpawnAt, pipeSpawnAt, MAX_TOOL_BOX_SPAWNS, MAX_PIPE_SPAWNS, withCellAsCleared, withCellPatched, withCellRemoved, withConnected, withGate, withGateAt, withGateClearedAt, gateMaxLengthAt, gateFitsAt, GATE_LENGTH, withDifficulty, withGeneration, withLockPanel, DEFAULT_LOCK_COUNTER, lockIndexAt, withLockCounter, withModifiersClearedAt, withTunnelSpawnCount, withTunnelSpawn, withSuperTunnelSpawnCount, withSuperTunnelSpawn, superTunnelSpawnAt, MAX_SUPER_TUNNEL_SPAWNS, isWall, withMechanicPainted, withName, moveGroupCells, duplicateCell, isConnectable, BOARD_SIZE, MAX_TUNNEL_SPAWNS, tunnelSpawnAt, LOCK_SIZE, PAINTABLE_MECHANICS, } from "../editor/templateOps";
import { allCells, cellKey, cellsInRect, copySelection, deleteSelection, duplicateSelection, moveSelection, pasteClipboard, selectedItemCells, } from "../editor/selectionOps";
import { validateTemplate, hasErrors, ValidationSeverity, MAX_PALETTE, WEDGE_COUNT } from "../editor/validateTemplate";
import { laserKeyProblem, modifierLabel, runLength, runProblem, sealIndicesAt, singleCoverProblem, withRunFlipped, withBolt, withMetalBox, withMetalHits, withDoorLength, withIceHits, withLaserGate, withLaserPairColor, withModifierRemoved, withSingleCover, withSlidingDoor, withElevator, withElevatorSpawn, elevatorSpawnAt, } from "../editor/sealOps";
import { normalizeTemplate } from "../editor/normalizeTemplate";
import { clearDraft, readDraft, writeDraft } from "../editor/draftStorage";
import { compileLevelWithReport, getEditorLevelContent, listEditorLevels, publishLevel, saveEditorLevel } from "../ContentApi";
import { containsLevel, levelDisplayName, neighbourLevel, sortLevelsNaturally } from "../editor/levelNav";
import { targetStore } from "../targetStore";
import { TargetChip } from "../TargetChip";
import { downloadBytes, NATIVE_EXPORT_HEIGHT, NATIVE_EXPORT_WIDTH, unityBridge, } from "../UnityBridge";
import { colorHexOf } from "../art/cellArt";
import { MarbleColor } from "../Models/LevelData";
import { paletteStore } from "../paletteStore";
import { GridDirection } from "../Models/CellData";
import { BOLT_LENGTH, CellModifierType, ELEVATOR_SIZE, FROZEN_BOX_HITS, LASER_PAIR_COLORS, METAL_BOX_HITS, SLIDING_DOOR_MIN_LENGTH } from "../Models/CellModifier";
import { LevelDifficulty } from "../Models/Editor/EditorLevel";
const SELECT_TOOL = { id: "select", label: "Select", help: "click an item to select it, drag to box-select, drag inside the selection to move it" };
// `badge` is a shortcut that is not a ⇧-digit (Select's plain V); the digit lookup reads `key` only.
const MECHANICS = [
    { ...SELECT_TOOL, badge: "V" },
    { id: "box", label: "Box", key: "1" },
    { id: "mystery", label: "Mystery", key: "2" },
    { id: "arrow", label: "Arrow", key: "3" },
    // A box FLAG like mystery and arrow: the box keeps them, and gains three colour columns.
    { id: "split", label: "Split Box", help: "a box in three colours, one per column: three screws of each (s toggles it on selected boxes)" },
    // A box holding a closed box of another colour, which plays first. A plain box otherwise: placing one takes
    // the mystery/arrow/glide/split flags off, and setting one of those turns it back into an ordinary box.
    { id: "double", label: "Double Box", help: "a box with a closed box of another colour inside it, which plays first (b toggles it on selected boxes)" },
    { id: "jumpingJack", label: "Jumping Jack", help: "a box whose spring launches its screws up high, so a box it opens can reach the throat first (j toggles it on selected boxes)" },
    { id: "tunnel", label: "Tunnel", key: "4" },
    { id: "gate", label: "Gate", key: "5" },
    { id: "lock", label: "Lock", key: "6" },
    { id: "connect", label: "Connect", key: "7" },
    { id: "color", label: "Colour", key: "8" },
    { id: "superTunnel", label: "Super Tunnel" },
    { id: "ice", label: "Ice" },
    { id: "superMystery", label: "Super Mystery" },
    { id: "door", label: "Sliding Door" },
    { id: "toolBox", label: "Tool Box" },
    { id: "pipe", label: "Pipe", help: "a tunnel feeding the cell below it, its body running up the column above into the top wall" },
    { id: "propeller", label: "Propeller", help: "tied to the box beside it: drop that box and it flies to a random box and blows it up, its screws going straight to the slices (arrow keys pick the box)" },
    { id: "elevator", label: "Elevator", help: "a 2×2 lift under four boxes; once all four have dropped it raises four more" },
    { id: "metalBox", label: "Metal Box" },
    { id: "bolt", label: "Bolt" },
    { id: "laser", label: "Laser Gate" },
    // Not a box: ground with nothing on it, as if a box had dropped and taken its socket with it — the wall
    // stays off the cell and the reveal runs through it from the start.
    { id: "cleared", label: "Cleared", key: "9", help: "board ground with nothing on it: no wall, no box, no socket" },
];
// How the palette groups the tools on screen: what a tool IS, so an author looking for "the thing that covers
// a box" finds all of them side by side. A mechanic left out of every group still shows, under Other.
const PALETTE_GROUPS = [
    { label: "Edit", ids: ["select"] },
    { label: "Board", ids: ["box", "mystery", "arrow", "split", "double", "jumpingJack", "tunnel", "gate", "propeller", "cleared"] },
    { label: "Spawners", ids: ["superTunnel", "toolBox", "pipe", "elevator"] },
    { label: "Seals", ids: ["lock", "ice", "superMystery", "metalBox", "bolt", "door", "laser"] },
    { label: "Links & paint", ids: ["connect", "color"] },
];
// What each click of a multi-click tool picks, in order — the words drawn on the board over each point and
// shown in the toolbar while the tool waits for the next one.
const RUN_STEPS = {
    door: ["ANCHOR", "FAR END"],
    laser: ["END", "END", "KEY"],
    bolt: ["HEAD", "FAR END"],
};
// The name a multi-click tool goes by in hints and menu rows.
const RUN_NAMES = { door: "Sliding door", laser: "Laser gate", bolt: "Bolt" };
function isRunTool(m) {
    return m === "door" || m === "laser" || m === "bolt";
}
// The inspector's names for a split box's columns, left to right.
const SPLIT_COLUMN_NAMES = ["Left", "Middle", "Right"];
// "x,y" -> a compiled split box's column colours, for the board preview. Only cells the game will draw split
// (all three colours set, CellData.HasSplitColors) — anything less it draws as a plain box of its colour.
function splitColorsOf(level) {
    const map = new Map();
    for (const c of level.cells.items) {
        const cols = c.splitColors;
        if (c.isSplit && Array.isArray(cols) && cols.length === SPLIT_COLUMNS && cols.every((k) => k !== MarbleColor.None)) {
            map.set(`${c.x},${c.y}`, cols);
        }
    }
    return map;
}
// "x,y" -> a compiled double box's inside colour, for the board preview. Only cells the game builds as double
// boxes (CellData.IsDouble: both colours set).
function innerColorsOf(level) {
    const map = new Map();
    for (const c of level.cells.items) {
        const inner = c.innerColor ?? MarbleColor.None;
        if (inner !== MarbleColor.None && c.color !== MarbleColor.None) {
            map.set(`${c.x},${c.y}`, inner);
        }
    }
    return map;
}
// A double box is a plain box holding another, so setting another box flag on one makes it an ordinary box
// again (templateOps.doublePatch takes those flags off the other way round).
function leavingDouble(c, flagOn) {
    return flagOn && c.isDouble ? { isDouble: false, innerColorOverride: MarbleColor.None } : {};
}
function leavingJack(c, flagOn) {
    return flagOn && c.isJumpingJack ? { isJumpingJack: false } : {};
}
const DIRECTIONS = [
    { v: GridDirection.Top, label: "Up" },
    { v: GridDirection.Bottom, label: "Down" },
    { v: GridDirection.Left, label: "Left" },
    { v: GridDirection.Right, label: "Right" },
];
export class LevelEditorPage extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            template: createEmptyTemplate(BOARD_SIZE, BOARD_SIZE),
            mechanic: "box",
            selected: null,
            region: [],
            marquee: null,
            pending: null,
            runPending: null,
            previewColors: null,
            compiled: null,
            balance: null,
            lastColors: null,
            paintColor: MarbleColor.Red01,
            compiling: false,
            compileError: null,
            unityReady: unityBridge.isReady,
            exportingWalls: false,
            saving: false,
            saveStatus: null,
            hint: null,
            past: [],
            future: [],
            showJson: false,
            mysteryDrafts: Array(WEDGE_COUNT).fill(null),
            menu: null,
            dupCount: 3,
            loadIssues: [],
            draftOffer: null,
            draftDiscardArmed: false,
            navLevels: null,
            navArmed: null,
            navigating: false,
        };
        // The template as it was last opened or saved — what the environment holds for this level. The board
        // has unsaved work exactly when the live template is a different object: every edit makes a new one,
        // and undoing back to here lands on this same object again. Its name is also where ‹ › stand in the
        // list, because the name field may hold a rename the environment has not seen yet.
        this.cleanTemplate = this.state.template;
        this.navListToken = 0;
        // Bumped by every load. A save started before one must not mark the level that replaced it as saved.
        this.loadGeneration = 0;
        // The level the red warning named when ‹ › were armed. The second press re-lists, and if that list now
        // puts a different level there, it asks again rather than dropping the work for a level never named.
        this.armedNeighbourId = null;
        this.unsubscribe = null;
        this.draftTimer = null;
        // Live-drag state: the cell the drag was grabbed at, what is being dragged (a region of cells — one item's
        // cells, or the whole selection), how far it has got, and the template before the drag (every step is
        // computed from it, and it is the one undo entry the whole drag leaves).
        this.dragOrigin = null;
        this.dragRegion = [];
        this.dragMulti = false;
        this.dragSelected = null;
        this.dragDelta = { x: 0, y: 0 };
        this.dragUndoSnapshot = null;
        // Box-select in progress: what was selected before it when it adds (⇧), so the rectangle can grow and
        // shrink over the top of it.
        this.marqueeBase = null;
        this.marqueeAnchor = null;
        // ⌘C / ⌘X. Held on the page, not in the template, so it survives opening another level — copying a
        // block from one level into the next is the point.
        this.clipboard = null;
        // Where the pointer is over the board, for ⌘V ("paste here"). Not state: nothing renders from it.
        this.hoverCell = null;
        // Paint state: the template before the gesture — one undo entry for the whole stroke, and the yardstick
        // for "did this stroke paint anything" when it ends (the ops return the same reference when they place
        // nothing, so identity answers it without a flag).
        this.paintUndoSnapshot = null;
        // Click-to-select guard for the per-wedge mystery-chance inputs (renderGeneration) — one instance per
        // wedge, since SelectOnClickGuard holds one input's worth of state. Those inputs don't use NumberInput
        // (they need decimal + comma-separator draft handling of their own) but hit the exact same Blink/WebKit
        // bug, so they share NumberInput's fix instead of carrying a second copy of it.
        this.mysteryPercentSelectGuards = Array.from({ length: WEDGE_COUNT }, () => new SelectOnClickGuard());
        this.undo = () => {
            this.setState((s) => s.past.length === 0
                ? null
                : this.stepTo(s, s.past[s.past.length - 1], { past: s.past.slice(0, -1), future: [s.template, ...s.future] }));
            this.scheduleDraftSave();
            this.scheduleRecompileIfColoursAreShown();
        };
        this.redo = () => {
            this.setState((s) => s.future.length === 0 ? null : this.stepTo(s, s.future[0], { past: [...s.past, s.template], future: s.future.slice(1) }));
            this.scheduleDraftSave();
            this.scheduleRecompileIfColoursAreShown();
        };
        this.clearBoard = () => {
            // "Clear the board" means the cells — difficulty and the generation params are level settings the
            // author tuned separately and should survive.
            const t = this.state.template;
            if (t.cells.items.length === 0 && t.cells.modifiers.length === 0) {
                return; // already empty: a fresh `cells` object would still be a NEW template, and setTemplate
                // would push an undo entry whose undo changes nothing the author can see.
            }
            this.setTemplate({ ...t, cells: { items: [], modifiers: [] } }, null);
        };
        // Loads the offered draft like any other edit — undoable, and it drops the compiled preview because
        // the board it described is gone.
        this.restoreDraft = () => {
            const offer = this.state.draftOffer;
            if (!offer) {
                return;
            }
            // Normalized like any other load: readDraft checks the draft's outer shape, not the cells inside it,
            // and a draft can hold a template that was written from a malformed Drive file before this repair
            // existed — storage outlives a deploy.
            const restored = normalizeTemplate(offer.template);
            this.setState({ draftOffer: null, draftDiscardArmed: false, loadIssues: restored.repairs });
            this.setTemplate(restored.template, null);
        };
        // Throwing the draft away cannot be undone and the button sits right next to Restore, so the first
        // press only arms it — the second one does the deed. A confirm() dialog is not an option here: a modal
        // freezes the Unity iframe in the pane beside this one.
        this.discardDraft = () => {
            if (!this.state.draftDiscardArmed) {
                this.setState({ draftDiscardArmed: true });
                return;
            }
            clearDraft();
            this.setState({ draftOffer: null, draftDiscardArmed: false });
        };
        // Writes a pending draft NOW instead of waiting out its timer. The write is debounced by half a second,
        // so a refresh inside that window used to lose the last edit — precisely the stretch the crash mat is
        // there to cover. Bound to beforeunload (a synchronous localStorage write is fine there) and to unmount.
        this.flushDraft = () => {
            if (this.draftTimer !== null) {
                window.clearTimeout(this.draftTimer);
                this.draftTimer = null;
                writeDraft(this.state.template);
            }
        };
        // Nothing is sent when no target is picked: the backend would reject it anyway, and pulsing the
        // selector says where the problem is far better than an error string under the button.
        this.save = async () => {
            const target = targetStore.id;
            if (target === null) {
                targetStore.requestAttention();
                this.setState({ saveStatus: "Pick a target first — nothing was saved." });
                return;
            }
            this.setState({ saving: true, saveStatus: null });
            const saved = this.state.template;
            const generation = this.loadGeneration;
            try {
                // `saved`, not `this.state.template`: the board stays live for the round trip, so the two are
                // no longer the same object by the time the reply lands — and the draft bookkeeping below has
                // to be about the template that actually went to Drive.
                const r = await saveEditorLevel(saved, target);
                this.clearDraftIfUnchanged(saved);
                this.markSaved(saved, generation);
                // The offer strip goes with the draft it was offering: left up, it sits over freshly saved work
                // and its Restore button overwrites that work with the stale draft it still holds.
                this.setState({ saving: false, saveStatus: `Saved ${r.name} → ${target}`, draftOffer: null, draftDiscardArmed: false });
            }
            catch (e) {
                this.setState({ saving: false, saveStatus: e instanceof Error ? e.message : "Save failed." });
            }
        };
        this.publish = async () => {
            const target = targetStore.id;
            if (target === null) {
                targetStore.requestAttention();
                this.setState({ saveStatus: "Pick a target first — nothing was published." });
                return;
            }
            this.setState({ saving: true, saveStatus: null });
            const saved = this.state.template;
            const generation = this.loadGeneration;
            try {
                const r = await publishLevel(saved, target); // see save() for why `saved` and not the live state
                this.clearDraftIfUnchanged(saved);
                this.markSaved(saved, generation);
                this.setState({ saving: false, saveStatus: `Published ${r.name} → ${target}`, draftOffer: null, draftDiscardArmed: false });
            }
            catch (e) {
                this.setState({ saving: false, saveStatus: e instanceof Error ? e.message : "Publish failed." });
            }
        };
        // --- ‹ › : the previous / next template of the selected environment ---------------------------------
        this.refreshNavLevels = async () => {
            const token = ++this.navListToken;
            try {
                const res = await listEditorLevels(targetStore.id);
                const levels = res.environmentFound === false ? null : sortLevelsNaturally(res.levels ?? []);
                if (token === this.navListToken) {
                    this.setState({ navLevels: levels });
                }
                return levels;
            }
            catch {
                if (token === this.navListToken) {
                    this.setState({ navLevels: null });
                }
                return null;
            }
        };
        this.stepLevel = async (step) => {
            if (this.state.navigating || this.state.saving) {
                return;
            }
            // Opening another level replaces the board, and the crash mat holds one draft for the whole editor —
            // the next edit on the new level overwrites it. So unsaved work is the one thing that asks first.
            const dirty = this.state.template !== this.cleanTemplate;
            if (dirty && this.state.navArmed !== step) {
                this.armedNeighbourId = this.navNeighbour(step)?.id ?? null;
                this.setState({ navArmed: step });
                return;
            }
            this.setState({ navigating: true, navArmed: null });
            // The board stays editable while the next level downloads; work done in that wait was never asked
            // about, so it cancels the step rather than being replaced.
            const boardAtClick = this.state.template;
            try {
                // Re-listed on the click rather than trusted from the last listing: someone else may have saved,
                // renamed or deleted a level in this environment since.
                const levels = await this.refreshNavLevels();
                const next = levels ? neighbourLevel(levels, this.cleanTemplate.name, step) : null;
                if (!next) {
                    this.setState({ navigating: false, hint: step > 0 ? "No next level in this environment." : "No previous level in this environment." });
                    return;
                }
                if (dirty && next.id !== this.armedNeighbourId) {
                    this.armedNeighbourId = next.id;
                    this.setState({ navigating: false, navArmed: step }); // the warning re-renders with the new name
                    return;
                }
                const text = await getEditorLevelContent(next.id);
                if (this.state.template !== boardAtClick) {
                    this.setState({ navigating: false, hint: `Not opened ${levelDisplayName(next.name)} — the board was edited while it loaded.` });
                    return;
                }
                this.loadTemplate(JSON.parse(text));
                this.setState({ navigating: false });
            }
            catch (e) {
                this.setState({ navigating: false, saveStatus: e instanceof Error ? e.message : "Opening the level failed." });
            }
        };
        // Left-click on an item selects it; left-click on an EMPTY cell places the active mechanic — the
        // palette says what a click puts down, and a click is just the one-cell case of the paint drag that
        // already works this way. Escape clears the selection, since an empty cell no longer does.
        //
        // ⇧-click adds the item to the selection or takes it back out, whatever the tool. With the Select tool a
        // click on an empty cell clears the selection instead of placing anything.
        this.onLeftClick = (x, y, shift) => {
            if (shift) {
                this.toggleInSelection(x, y);
                return;
            }
            // With the Colour tool held, a click paints what is there. Selecting is what a click does the rest
            // of the time — and painting selects too, so the inspector follows the box being worked on.
            if (this.state.mechanic === "color") {
                this.paint(withCellColor(this.state.template, x, y, this.state.paintColor), { x, y });
                return;
            }
            // A multi-click tool picks CELLS — occupied ones above all (a door lies over boxes, a key is a box) —
            // so every click is a point, never a selection.
            if (isRunTool(this.state.mechanic)) {
                this.addRunPoint(this.state.mechanic, x, y);
                return;
            }
            // Ice and the super mystery box go OVER a box, so a click on one covers it (and selects it, so the
            // inspector follows). Clicking an empty cell lays a box with the cover on it.
            if (this.state.mechanic === "ice" || this.state.mechanic === "superMystery" || this.state.mechanic === "metalBox" || this.state.mechanic === "elevator") {
                this.applyMechanic(this.state.mechanic, x, y);
                return;
            }
            if (findCell(this.state.template, x, y)) {
                this.setState({ selected: { x, y }, region: [] });
                return;
            }
            if (this.state.mechanic === "select") {
                this.setState({ selected: null, region: [] });
                return;
            }
            this.applyMechanic(this.state.mechanic, x, y);
        };
        // --- box-select --------------------------------------------------------------------------------------
        // The anchor lives on the instance, not in state: the canvas reports the start and the first corner in
        // the same mousemove, before React has applied the start.
        this.onMarqueeStart = (cell, additive) => {
            this.marqueeBase = additive ? this.selectionCells() : [];
            this.marqueeAnchor = cell;
            this.setState({ menu: null });
            this.onMarqueeOver(cell);
        };
        this.onMarqueeOver = (cell) => {
            const base = this.marqueeBase;
            const a = this.marqueeAnchor;
            if (!base || !a) {
                return;
            }
            const t = this.state.template;
            const seen = new Set(base.map((c) => cellKey(c.x, c.y)));
            const region = [...base, ...cellsInRect(t, a, cell).filter((c) => !seen.has(cellKey(c.x, c.y)))];
            const items = selectedItemCells(t, region);
            this.setState({ marquee: { a, b: cell }, region, selected: items[0] ?? null, pending: null });
        };
        this.onMarqueeEnd = () => {
            this.marqueeBase = null;
            this.marqueeAnchor = null;
            this.setState({ marquee: null });
        };
        this.onCellHover = (cell) => {
            this.hoverCell = cell;
        };
        // Right-click opens the menu for that cell. Placing, deleting and duplicating all happen from there.
        // The exception is the Colour tool, where right-click is the eraser — the same left-paints /
        // right-clears pairing the slice grid uses, so the two surfaces behave identically.
        this.onRightClick = (x, y, screenX, screenY) => {
            if (this.state.mechanic === "color") {
                this.paint(withCellColor(this.state.template, x, y, MarbleColor.None), { x, y });
                return;
            }
            this.setState({ menu: { x, y, screenX, screenY } });
        };
        this.paintSlice = (wedge, depth, color) => {
            this.paint(withSliceColor(this.state.template, wedge, depth, color));
        };
        // Freezing is an edit like a paint: undoable, and it recompiles so the preview carries the ice.
        this.freezeSlice = (wedge, depth, frozen) => {
            this.paint(withSliceFrozen(this.state.template, wedge, depth, frozen));
        };
        this.thawAllSlices = () => {
            this.paint(withoutFrozenSlices(this.state.template));
        };
        // Runs a menu row against the cell the menu was opened on, then closes the menu.
        this.onMenuSelect = (id) => {
            const menu = this.state.menu;
            if (!menu) {
                return;
            }
            const { x, y } = menu;
            const t = this.state.template;
            this.setState({ menu: null });
            switch (id) {
                case "selDuplicate":
                    this.duplicateRegion();
                    return;
                case "selCopy":
                    this.copySelected();
                    return;
                case "selCut":
                    this.cutSelected();
                    return;
                case "selDelete":
                    this.deleteSelected();
                    return;
                case "paste":
                    this.pasteAt({ x, y });
                    return;
                case "delete": {
                    const existing = findCell(t, x, y);
                    if (!existing) {
                        return;
                    }
                    const removed = existing.isGate ? withGateClearedAt(t, x, y) : withModifiersClearedAt(withCellRemoved(t, x, y), x, y);
                    // The selection is diffed the same way `pending` is below, and for the same reason: a gate
                    // deletion empties cells the author never clicked on, and a selection left sitting on one of
                    // them is a live target for ⇧N — which would author a cell exactly where one was deleted.
                    const s = this.state.selected;
                    const sel = s && findCell(t, s.x, s.y) && !findCell(removed, s.x, s.y) ? null : s;
                    // A gate deletion clears the WHOLE gate — head plus every continuation cell — not just the
                    // cell that was clicked, so `pending` can be dangling even when it doesn't match (x, y). Diff
                    // the template before/after instead of comparing coordinates: if the pending cell held
                    // something before this delete and holds nothing after, the deletion took it, whatever shape
                    // that removal had (single cell today, a gate span here, anything multi-cell later) — without
                    // duplicating withGateClearedAt's span logic in the page.
                    const p = this.state.pending;
                    if (p && findCell(t, p.x, p.y) && !findCell(removed, p.x, p.y)) {
                        this.setState({ pending: null });
                    }
                    this.setTemplate(removed, sel);
                    return;
                }
                case "duplicate":
                    this.duplicateAt(x, y, 1);
                    return;
                case "duplicateN":
                    this.duplicateAt(x, y, this.state.dupCount);
                    return;
                case "connectTo": {
                    const p = this.state.pending;
                    if (!p) {
                        return;
                    }
                    // Re-check the pending cell is still a box you can link, even though "delete" above already
                    // clears `pending` when it deletes that exact cell: this is defence in depth for any other
                    // way it could stop being one between the two clicks (undo, a load from another tab, ⇧5
                    // stamping a gate over it) — the validator only WARNS about a bad link, never errors, so one
                    // would otherwise save, compile and publish unnoticed. The far end is checked too: menuItems
                    // won't offer the row over a wall, but nothing here should depend on that.
                    if (!isConnectable(t, p.x, p.y) || !isConnectable(t, x, y)) {
                        this.setState({ pending: null });
                        return;
                    }
                    if (!(p.x === x && p.y === y)) {
                        this.setState({ pending: null });
                        this.setTemplate(withConnected(t, p.x, p.y, x, y));
                    }
                    return;
                }
                case "door":
                case "laser":
                case "bolt":
                    // Starting from the menu switches to that tool, so the next points can be clicked.
                    if (this.state.mechanic !== id) {
                        this.setState({ mechanic: id });
                    }
                    this.addRunPoint(id, x, y);
                    return;
                default:
                    this.applyMechanic(id, x, y);
                    return;
            }
        };
        // --- live drag-to-move ---------------------------------------------------------------------------
        // The grabbed item — or the whole selection, when the item is part of a larger one — follows the cursor:
        // it moves the moment it fits, without waiting for the drop. Whole gates and connected pairs move
        // together; a spot where it does not fit leaves it at the last one that did. Every step is worked out
        // from the pre-drag board, so the drag can wander through spots that don't fit and come out the other
        // side. The whole gesture collapses into a single undo entry.
        this.onCellDragStart = (cell) => {
            const t = this.state.template;
            const current = this.selectionCells();
            const inSelection = current.some((c) => c.x === cell.x && c.y === cell.y);
            if (inSelection) {
                this.dragRegion = current;
                this.dragMulti = this.state.region.length > 0;
                this.dragSelected = this.state.selected;
            }
            else if (findCell(t, cell.x, cell.y)) {
                // An item outside the selection: grabbing it selects it, and it alone moves.
                this.dragRegion = moveGroupCells(t, cell.x, cell.y);
                this.dragMulti = false;
                this.dragSelected = { x: cell.x, y: cell.y };
                this.setState({ selected: { x: cell.x, y: cell.y }, region: [] });
            }
            else {
                return; // nothing to drag
            }
            this.dragOrigin = { x: cell.x, y: cell.y };
            this.dragDelta = { x: 0, y: 0 };
            this.dragUndoSnapshot = t;
        };
        this.onCellDragOver = (cell) => {
            const origin = this.dragOrigin;
            const snap = this.dragUndoSnapshot;
            if (!origin || !snap) {
                return;
            }
            const dx = cell.x - origin.x;
            const dy = cell.y - origin.y;
            if (dx === this.dragDelta.x && dy === this.dragDelta.y) {
                return;
            }
            const sel = this.dragSelected;
            if (dx === 0 && dy === 0) {
                // Back to where it started: the pre-drag board, exactly.
                this.dragDelta = { x: 0, y: 0 };
                this.setState({ template: snap, selected: sel, region: this.dragMulti ? this.dragRegion : [], previewColors: null, compiled: null, balance: null, compileError: null });
                return;
            }
            const r = moveSelection(snap, this.dragRegion, dx, dy);
            if (!r) {
                return; // doesn't fit here — leave it at its last valid spot
            }
            this.dragDelta = { x: dx, y: dy };
            // Moved live without pushing per-step undo entries (the drag commits one entry on end).
            this.setState({
                template: r.template,
                selected: sel ? { x: sel.x + dx, y: sel.y + dy } : null,
                region: this.dragMulti ? r.region : [],
                previewColors: null,
                compiled: null,
                balance: null,
                compileError: null,
            });
        };
        this.onCellDragEnd = () => {
            const snap = this.dragUndoSnapshot;
            if (snap && this.dragDelta.x === 0 && this.dragDelta.y === 0) {
                // Back where it started, or it never fitted anywhere: no undo entry, since its undo would visibly
                // do nothing. Putting the pre-drag template back also restores the reference every
                // identity-based guard downstream (setTemplate, onlyDifficultyDiffers) reads.
                if (this.state.template !== snap) {
                    this.setState({ template: snap });
                }
            }
            else if (snap) {
                this.setState((s) => ({ past: [...s.past, snap].slice(-50), future: [] }));
            }
            this.dragOrigin = null;
            this.dragRegion = [];
            this.dragMulti = false;
            this.dragSelected = null;
            this.dragDelta = { x: 0, y: 0 };
            this.dragUndoSnapshot = null;
            this.scheduleDraftSave();
        };
        this.onCellPaintStart = (cell) => {
            if (this.state.mechanic !== "color" && !LevelEditorPage.isPaintable(this.state.mechanic)) {
                return;
            }
            this.paintUndoSnapshot = this.state.template;
            this.paintAt(cell.x, cell.y);
        };
        this.onCellPaintOver = (cell) => {
            if (this.paintUndoSnapshot) {
                this.paintAt(cell.x, cell.y);
            }
        };
        this.onCellPaintEnd = () => {
            const snap = this.paintUndoSnapshot;
            // "Did this stroke paint anything" is answered here, at the end, by comparing the template against
            // the pre-stroke snapshot — NOT by a flag set inside paintAt's updater. StrictMode double-invokes
            // updaters, so a flag written in one is set twice per painted cell and can be set by a call whose
            // result React then throws away; template identity cannot lie about it.
            if (snap && this.state.template !== snap) {
                this.setState((s) => ({ past: [...s.past, snap].slice(-50), future: [] }));
            }
            this.paintUndoSnapshot = null;
            this.scheduleDraftSave();
        };
        this.onKeyDown = (e) => {
            if (!this.props.active) {
                return;
            }
            // A live drag or paint stroke owns the board until the mouse says otherwise. A shortcut fired
            // mid-gesture scrambles the undo stack: it pushes an entry for the MID-stroke template, and then the
            // gesture end pushes its own pre-stroke snapshot on top of that — so the first ⌘Z jumps back past
            // the whole stroke and the second walks FORWARD into the middle of it.
            if (this.dragOrigin || this.paintUndoSnapshot || this.marqueeBase) {
                return;
            }
            // Escape is handled BEFORE the typing guard. The context menu carries its own number box, so the
            // menu can be open with focus sitting inside an input — which is exactly when "get me out of here"
            // is wanted, and where the guard used to swallow it.
            if (e.key === "Escape") {
                // Also drops the selection: clicking an empty cell now places a mechanic instead of clearing it.
                this.setState({ menu: null, pending: null, runPending: null, selected: null, region: [] });
                return;
            }
            if (this.ignoresHotkeys()) {
                return;
            }
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                // The two guards the Compile button carries, which this shortcut went around. On a board the
                // validator has already rejected it fired off a request the backend answers with a 400, and the
                // failure path clears the preview — so ⌘⏎ threw away the author's working preview to show an
                // error the Issues panel was already showing. Held down, it also started overlapping compiles,
                // and each reply wrote its own result; a slower earlier one landing last left Play sending a
                // board that was not the one just compiled.
                if (this.state.compiling) {
                    return;
                }
                if (hasErrors(validateTemplate(this.state.template))) {
                    this.setState({ hint: "Fix the errors first — see the Issues panel." });
                    return;
                }
                void this.compileAndPlay();
                return;
            }
            if ((e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z")) {
                e.preventDefault();
                this.setState({ menu: null });
                if (e.shiftKey) {
                    this.redo();
                }
                else {
                    this.undo();
                }
                return;
            }
            if ((e.metaKey || e.ctrlKey) && (e.key === "y" || e.key === "Y")) {
                e.preventDefault();
                this.setState({ menu: null });
                this.redo();
                return;
            }
            // Selection keys: ⌘A all, ⌘C / ⌘X / ⌘V clipboard (paste lands where the pointer is), Delete.
            if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
                const k = e.key.toLowerCase();
                if (k === "a") {
                    e.preventDefault();
                    this.selectRegion(allCells(this.state.template));
                    return;
                }
                if (k === "c") {
                    e.preventDefault();
                    this.copySelected();
                    return;
                }
                if (k === "x") {
                    e.preventDefault();
                    this.cutSelected();
                    return;
                }
                if (k === "v") {
                    e.preventDefault();
                    this.pasteAt(this.hoverCell);
                    return;
                }
            }
            if ((e.key === "Delete" || e.key === "Backspace") && !e.metaKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault();
                this.deleteSelected();
                return;
            }
            const dir = LevelEditorPage.ARROW_KEYS[e.key];
            if (dir !== undefined && !e.metaKey && !e.ctrlKey && !e.altKey) {
                // ⇧+arrow nudges the selection one cell; a bare arrow aims every selected tunnel, arrow and propeller
                // (a propeller's aim is the box it is tied to).
                if (e.shiftKey) {
                    if (this.selectionCells().length > 0) {
                        e.preventDefault();
                        const step = { [GridDirection.Top]: [0, -1], [GridDirection.Bottom]: [0, 1], [GridDirection.Left]: [-1, 0], [GridDirection.Right]: [1, 0] }[dir];
                        if (!this.moveSelectionBy(step[0], step[1])) {
                            this.setState({ hint: "Can't move that way — something is in the way, or it's the edge." });
                        }
                    }
                    return;
                }
                // Held arrow keys native-repeat many keydowns/sec. withCellPatched has no same-value guard, so
                // without the direction check every repeat — even ones that don't change the direction — would
                // push a fresh no-op entry onto the (50-capped) undo stack and evict real history.
                const aimed = this.patchSelection((c) => c.isTunnel || (c.isPropeller ?? false) || (c.isArrow && !c.isGate), (c) => c.isTunnel
                    ? c.tunnelDirection !== dir ? { tunnelDirection: dir } : null
                    : c.isPropeller
                        ? (c.propellerDirection ?? GridDirection.Top) !== dir ? { propellerDirection: dir } : null
                        : c.arrowDirection !== dir ? { arrowDirection: dir } : null);
                if (aimed) {
                    e.preventDefault();
                }
                return;
            }
            if (e.key === "d" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                e.preventDefault();
                if (this.state.region.length > 0) {
                    this.duplicateRegion();
                }
                else {
                    this.duplicateSelected(1);
                }
                return;
            }
            if (e.key === "v" && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey) {
                e.preventDefault();
                this.setState({ mechanic: "select", pending: null, runPending: null });
                return;
            }
            // The same modifier guard the letter and digit branches carry: without it ⌘⇧1 / Ctrl+Shift+1 /
            // ⌥⇧1 were swallowed from the browser and authored a cell.
            if (e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const picked = MECHANICS.find((m) => m.key !== undefined && m.key === e.code.replace("Digit", ""));
                if (picked) {
                    e.preventDefault();
                    this.setState({ mechanic: picked.id, pending: null, runPending: null });
                    const s = this.state.selected;
                    if (s) {
                        this.applyMechanic(picked.id, s.x, s.y);
                    }
                    return;
                }
            }
            // m and the digits act on every selected box. m sets them all the same way — the opposite of the
            // box the inspector shows — rather than flipping each, so a mixed selection ends up uniform.
            const isBox = (c) => !isWall(c);
            if (e.key === "m" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const lead = this.selectedCell();
                const on = !(lead && isBox(lead) ? lead.isMystery : this.selectionItems().every((c) => findCell(this.state.template, c.x, c.y)?.isMystery));
                if (this.patchSelection(isBox, (c) => (c.isMystery !== on ? { isMystery: on, ...leavingDouble(c, on) } : null))) {
                    e.preventDefault();
                }
                return;
            }
            // g: glide, the same way m does mystery — every selected box set alike.
            if (e.key === "g" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const lead = this.selectedCell();
                const on = !(lead && isBox(lead) ? (lead.isGlide ?? false) : this.selectionItems().every((c) => findCell(this.state.template, c.x, c.y)?.isGlide));
                if (this.patchSelection(isBox, (c) => ((c.isGlide ?? false) !== on ? { isGlide: on, ...leavingDouble(c, on), ...leavingJack(c, on) } : null))) {
                    e.preventDefault();
                }
                return;
            }
            // s: split, the same way again. splitPatch carries each box's paint across (its one colour becomes
            // the middle column, and back).
            if (e.key === "s" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const lead = this.selectedCell();
                const on = !(lead && isBox(lead) ? (lead.isSplit ?? false) : this.selectionItems().every((c) => findCell(this.state.template, c.x, c.y)?.isSplit));
                if (this.patchSelection(isBox, (c) => splitPatch(c, on))) {
                    e.preventDefault();
                }
                return;
            }
            // b: double box (a box in a box), the same way again. doublePatch takes the other box flags off.
            if (e.key === "b" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const lead = this.selectedCell();
                const on = !(lead && isBox(lead) ? (lead.isDouble ?? false) : this.selectionItems().every((c) => findCell(this.state.template, c.x, c.y)?.isDouble));
                if (this.patchSelection(isBox, (c) => doublePatch(c, on))) {
                    e.preventDefault();
                }
                return;
            }
            if (e.key === "j" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const lead = this.selectedCell();
                const on = !(lead && isBox(lead) ? (lead.isJumpingJack ?? false) : this.selectionItems().every((c) => findCell(this.state.template, c.x, c.y)?.isJumpingJack));
                if (this.patchSelection(isBox, (c) => jackPatch(c, on))) {
                    e.preventDefault();
                }
                return;
            }
            if (e.key >= "0" && e.key <= "9" && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey) {
                const group = parseInt(e.key, 10);
                if (this.patchSelection(isBox, (c) => (c.group !== group ? { group } : null))) {
                    e.preventDefault();
                }
                return;
            }
        };
        // Compiles the template and RETURNS the level it produced (null when the compile failed), on top of the
        // state updates the Compile button reads. Callers that need the result immediately must use the return
        // value: the success setState below lands in a promise continuation, which React schedules on the
        // default lane — a macrotask that runs after any `await this.compile()` resumes — so `this.state.compiled`
        // is still the PREVIOUS compile's value at that point.
        this.compile = async () => {
            // Only the buttons disable for the round trip — the BOARD stays live, so the author can keep
            // authoring while the request is out, and every board edit clears the preview on purpose. Letting a
            // late reply write its result back anyway painted colours from a board that no longer exists over
            // the new one, and left `compiled` holding a level Play would then send. Identity is the exact test
            // (every op returns a new template when and only when something changed) — the same one
            // clearDraftIfUnchanged uses for a save that lands after an edit.
            const requested = this.state.template;
            this.setState({ compiling: true, compileError: null });
            try {
                const { level, balance } = await compileLevelWithReport(requested);
                if (this.state.template !== requested) {
                    this.setState({ compiling: false });
                    return null;
                }
                const map = new Map();
                for (const c of level.cells.items) {
                    if (c.color !== MarbleColor.None) {
                        map.set(`${c.x},${c.y}`, c.color);
                    }
                }
                this.setState({ compiled: level, previewColors: map, balance, lastColors: { level, balance }, compiling: false });
                return level;
            }
            catch (e) {
                // A banner belongs to the board that failed. Undo already drops the banner when it lands on a
                // template the failed compile never saw (stepTo); a reply arriving after an edit is the same
                // situation from the other side.
                if (this.state.template !== requested) {
                    this.setState({ compiling: false });
                    return null;
                }
                // A failed compile must drop whatever the PREVIOUS successful compile left behind — otherwise
                // `compiled` still holds that old level, Play (and ⌘Enter's compileAndPlay) stays enabled, and
                // it silently replays the wrong board while a fresh error banner sits on screen.
                this.setState({
                    compileError: e instanceof Error ? e.message : "Compile failed.",
                    compiling: false,
                    compiled: null,
                    balance: null,
                    previewColors: null,
                });
                return null;
            }
        };
        this.play = () => {
            if (this.state.compiled) {
                unityBridge.playLevel(JSON.stringify(this.state.compiled));
            }
        };
        // ---- Wall PNG export ---------------------------------------------------------------------
        // The board's walls as one transparent PNG at the game's native canvas, for the art and porting
        // pipelines. The picture is made inside the player (BoardWallView.Export.cs) from the level it
        // is CURRENTLY playing — so this exports what is on the right, which is not necessarily what is
        // on the left: hit Play first if the board has moved on.
        this.exportWalls = () => {
            if (!unityBridge.exportWallPng(NATIVE_EXPORT_WIDTH, NATIVE_EXPORT_HEIGHT)) {
                this.setState({ hint: "The game is still loading." });
                return;
            }
            this.setState({ exportingWalls: true, hint: null });
        };
        // Names the download after the level, which only this side knows: the player is handed a board,
        // never its template's name.
        this.onUnityFile = (file) => {
            // Only a wall export THIS page asked for is its to download: level resources (screenshots, and
            // the walls exported with them) are claimed by their own requests.
            if (!this.state.exportingWalls || !file.name.startsWith("walls-")) {
                return;
            }
            const name = (this.state.template.name || "level").replace(/[^\w.-]+/g, "-");
            downloadBytes(file, `${name}-${file.name}`);
            this.setState({ exportingWalls: false, hint: `Downloaded ${name}-${file.name}` });
        };
        this.onUnityError = (kind, message) => {
            if (!this.state.exportingWalls || kind.startsWith("level-image")) {
                return;
            }
            this.setState({ exportingWalls: false, hint: `${kind}: ${message}` });
        };
        // Compile, then play what it produced — the loop the author repeats all day, on one key.
        //
        // Plays the level compile() RETURNED, never `this.state.compiled`: that state write has not landed yet
        // when this continuation resumes (see compile()), so reading it here played the previous compile's board
        // — and since every board edit clears `compiled`, the normal edit → ⌘Enter loop found null and never
        // played at all.
        this.compileAndPlay = async () => {
            const level = await this.compile();
            if (level && unityBridge.isReady) {
                unityBridge.playLevel(JSON.stringify(level));
            }
        };
        // Copies wedge 0's STORED rate, not the percent shown in its box — going through the display rounded
        // 0.335 to 0.34 in wedge 0 as well, so "all =" quietly edited the value it was supposed to copy.
        this.setMysteryChanceAll = () => {
            const arr = Array(WEDGE_COUNT).fill(this.mysteryChanceAt(0));
            if (arr.every((v, i) => v === this.mysteryChanceAt(i))) {
                return; // no-op: don't burn an undo entry or drop the compiled preview
            }
            this.setState({ mysteryDrafts: Array(WEDGE_COUNT).fill(null) });
            this.setTemplate(withGeneration(this.state.template, { mysterySliceChances: arr }));
        };
    }
    componentDidMount() {
        this.unsubscribe = unityBridge.subscribe(() => this.setState({ unityReady: unityBridge.isReady }));
        // The way back from the player: a finished file downloads, a refusal becomes a toolbar hint.
        // Both clear the exporting flag — whichever arrives, the click is over.
        this.unsubscribeFile = unityBridge.onFile(this.onUnityFile);
        this.unsubscribeUnityError = unityBridge.onError(this.onUnityError);
        // The selected colour preset decides which colour sits in each palette slot (the guaranteed-colour
        // chips read it). Loaded here rather than at app start so the fallback order shows only until the
        // server answers; the store re-renders this page when it does — or when Settings changes it.
        this.unsubscribePalette = paletteStore.subscribe(() => this.forceUpdate());
        void paletteStore.ensureLoaded();
        // ‹ › walk the selected environment's list — production when none is picked, the same list the Levels
        // page shows and opens from — so a new pick re-lists what they walk.
        this.unsubscribeTarget = targetStore.subscribe(() => void this.refreshNavLevels());
        void this.refreshNavLevels();
        window.addEventListener("keydown", this.onKeyDown);
        window.addEventListener("beforeunload", this.flushDraft);
        this.setState({ draftOffer: readDraft() });
    }
    componentDidUpdate(prev, prevState) {
        // A new template was opened from the Editor Levels tab — load it (reset preview/selection/history).
        if (this.props.loadToken !== prev.loadToken && this.props.loadTemplate) {
            this.loadTemplate(this.props.loadTemplate);
        }
        if (this.state.navArmed !== null && this.state.template !== prevState.template) {
            this.setState({ navArmed: null });
        }
    }
    // The one way a template from outside becomes the board: the Levels page's Open and the ‹ › buttons.
    loadTemplate(incoming) {
        // Anything still sitting on the debounced draft timer belongs to the level about to be replaced.
        // Left to fire, it writes whatever `this.state.template` holds BY THEN — the level being loaded
        // — so editing a level and opening another one inside that half second threw the first level's
        // unsaved work off the crash mat and put an "Unsaved draft" strip over a board nobody had
        // touched. save/publish already cancel the timer for exactly this reason; flushing rather than
        // cancelling is what keeps the work that was pending.
        this.flushDraft();
        // Through normalizeTemplate, because what arrives here is `JSON.parse(text) as EditorLevel` —
        // a cast, not a check — and the backend accepts shapes this editor's ops cannot read. This is
        // the one place a foreign template becomes editor state, which is what makes a single repair
        // here enough for templateOps, GridCanvas and every render method downstream.
        const loaded = normalizeTemplate(incoming);
        this.setState({
            template: loaded.template,
            loadIssues: loaded.repairs,
            selected: null,
            region: [],
            marquee: null,
            pending: null,
            runPending: null,
            // A menu left open from the previous level points at coordinates on a board that is gone,
            // and its rows would run against the one that just loaded.
            menu: null,
            previewColors: null,
            compiled: null,
            lastColors: null,
            compileError: null,
            saveStatus: null,
            past: [],
            future: [],
            mysteryDrafts: Array(WEDGE_COUNT).fill(null),
            // The draft strip was offering a save from whatever level was open before — it no longer
            // describes this one, and Restore must never apply level A's draft onto level B's board.
            draftOffer: null,
            draftDiscardArmed: false,
            navArmed: null,
        });
        this.cleanTemplate = loaded.template;
        this.loadGeneration++;
        void this.refreshNavLevels();
    }
    // Stepping to `next` keeps the compiled preview exactly when setMetadata would have — otherwise undoing
    // a difficulty change threw away a preview that making the change had deliberately kept. `compileError`
    // follows the same rule: undo can land on a template the failed compile never saw (break the board,
    // Compile, 400, Undo — the banner would describe the board you just left), so anything that drops the
    // preview must drop the banner with it.
    stepTo(s, next, history) {
        const keep = onlyDifficultyDiffers(next, s.template);
        return {
            ...s,
            ...history,
            template: next,
            selected: keep ? s.selected : null,
            region: keep ? s.region : [],
            previewColors: keep ? s.previewColors : null,
            compiled: keep ? s.compiled : null,
            balance: keep ? s.balance : null,
            compileError: keep ? s.compileError : null,
            draftDiscardArmed: false, // undo is authoring too — see setTemplate
            mysteryDrafts: Array(WEDGE_COUNT).fill(null),
        };
    }
    componentWillUnmount() {
        window.clearTimeout(this.recompileTimer); // a paint 180 ms before a tab switch would fire into a dead component
        this.unsubscribe?.();
        this.unsubscribeFile?.();
        this.unsubscribeUnityError?.();
        this.unsubscribePalette?.();
        this.unsubscribeTarget?.();
        window.removeEventListener("keydown", this.onKeyDown);
        window.removeEventListener("beforeunload", this.flushDraft);
        this.flushDraft();
    }
    // A save/publish that lands while a debounced draft-write is still pending must cancel it — otherwise
    // the timer fires after clearDraft() and writes the just-cleared draft straight back, and the very next
    // refresh shows "recoverable draft" over a level that was just cleanly saved.
    cancelPendingDraftSave() {
        if (this.draftTimer !== null) {
            window.clearTimeout(this.draftTimer);
            this.draftTimer = null;
        }
    }
    // Drops the crash mat for a template that has just been written to Drive — but only if the board still
    // holds that exact template. The Save/Publish buttons disable themselves for the round trip; the BOARD
    // does not, so the author can keep placing cells while it runs. Clearing unconditionally on the way back
    // threw those cells off the mat: they lived in React state alone until the next edit re-armed the timer,
    // and a refresh in between lost them without ever offering a draft. Identity is an exact test here —
    // every op returns a new template when and only when something changed.
    clearDraftIfUnchanged(saved) {
        if (this.state.template === saved) {
            this.cancelPendingDraftSave();
            clearDraft();
        }
        else {
            this.scheduleDraftSave(); // edited mid-save: keep the mat under the work that isn't saved
        }
    }
    // `saved` is what the environment holds now. A first save, or a save under a new name, also puts the
    // level somewhere in the list ‹ › walk, so that is re-read.
    markSaved(saved, generation) {
        if (generation !== this.loadGeneration) {
            return; // another level was opened while this one was saving — the board is no longer `saved`'s
        }
        this.cleanTemplate = saved;
        // A ‹ › armed over the work just saved was warning about work that is no longer unsaved.
        if (this.state.navArmed !== null && this.state.template === saved) {
            this.setState({ navArmed: null });
        }
        void this.refreshNavLevels();
    }
    navNeighbour(step) {
        return this.state.navLevels ? neighbourLevel(this.state.navLevels, this.cleanTemplate.name, step) : null;
    }
    renderNavButton(step) {
        const target = this.navNeighbour(step);
        const armed = this.state.navArmed === step;
        const title = target
            ? `${step > 0 ? "Next" : "Previous"}: ${levelDisplayName(target.name)}`
            : this.state.navLevels === null
                ? "The environment's levels could not be listed"
                : containsLevel(this.state.navLevels, this.cleanTemplate.name)
                    ? step > 0
                        ? "This is the last level"
                        : "This is the first level"
                    : targetStore.id === null
                        ? "This level is not in Production (no environment picked)"
                        : "This level is not saved in the selected environment";
        return (_jsx("button", { type: "button", onClick: () => void this.stepLevel(step), disabled: !target || this.state.navigating || this.state.saving, title: title, className: "rounded-md px-2 py-1 text-xs disabled:opacity-40 " +
                (armed ? "bg-red-600/70 text-white hover:bg-red-600" : "bg-slate-800 text-slate-300 hover:bg-slate-700"), children: step > 0 ? "›" : "‹" }));
    }
    // Writes the working template to storage shortly after the last edit. Debounced because a paint stroke
    // or a held-down digit would otherwise serialise the whole board on every keystroke.
    scheduleDraftSave() {
        if (this.draftTimer !== null) {
            window.clearTimeout(this.draftTimer);
        }
        this.draftTimer = window.setTimeout(() => {
            this.draftTimer = null;
            writeDraft(this.state.template);
        }, 500);
    }
    // Any board mutation invalidates the compiled preview and pushes onto the undo stack.
    // `region` is the selection the edit leaves behind; the default drops a multi-selection, since an edit of
    // one cell makes that cell the thing being worked on.
    setTemplate(next, selected = this.state.selected, region = []) {
        if (next === this.state.template) {
            return; // no-op edit (e.g. placing a box where one already is)
        }
        this.setState((s) => ({
            template: next,
            selected,
            region,
            previewColors: null,
            compiled: null,
            balance: null,
            compileError: null,
            // The hint describes the edit that produced it ("board full", "gate needs two free cells"), so
            // it belongs to that edit and not to the session: left standing it sat in the toolbar long
            // after the board it was about had changed. Callers that want a hint ABOUT this edit set it
            // after calling here.
            hint: null,
            // Discard is armed by its first press and there is no undo for what the second one does, so it
            // must not stay armed while the author goes on working — ten minutes later a stray click on a
            // strip still standing would throw the draft away with no second thought, which is the whole
            // thing the two-press guard exists to prevent.
            draftDiscardArmed: false,
            past: [...s.past, s.template].slice(-50),
            future: [],
        }));
        this.scheduleDraftSave();
    }
    // Edits to the fields OUTSIDE the board — difficulty and the level's name. Undoable like any other
    // change, and draft-saved like any other change; what it does not do is assume the compiled preview
    // died with it.
    //
    // It keeps the preview on exactly the same test stepTo uses to keep it across an undo, so making a
    // change and undoing it agree: a difficulty change is invisible to the compiler
    // (CompilerTests.Difficulty_DoesNotReachTheCompiledLevel), so preview, Play and the compileError banner
    // all still describe this template — clearing the banner would claim a failure went away. A rename is
    // not invisible (LevelCompiler.ResolveSeed reads the name under SeedStrategy.LevelNumber), so it drops
    // them, same as any board edit.
    setMetadata(next) {
        if (next === this.state.template) {
            return;
        }
        this.setState((s) => {
            const keep = onlyDifficultyDiffers(next, s.template);
            return {
                template: next,
                previewColors: keep ? s.previewColors : null,
                compiled: keep ? s.compiled : null,
                balance: keep ? s.balance : null,
                compileError: keep ? s.compileError : null,
                draftDiscardArmed: false, // see setTemplate
                past: [...s.past, s.template].slice(-50),
                future: [],
            };
        });
        this.scheduleDraftSave();
    }
    // --- selection -------------------------------------------------------------------------------------
    // The selection is either one item (`selected`, grown to the item's own cells — a whole gate) or a
    // region of cells (`region`). Everything that acts on "the selection" reads it through here.
    selectionCells(s = this.state) {
        if (s.region.length > 0) {
            return s.region;
        }
        if (s.selected) {
            const own = moveGroupCells(s.template, s.selected.x, s.selected.y);
            return own.length > 0 ? own : [s.selected];
        }
        return [];
    }
    // The items the selection holds (whole ones, in reading order).
    selectionItems() {
        return selectedItemCells(this.state.template, this.selectionCells());
    }
    // How many whole items an explicit multi-selection holds, or 0 when the selection is just the clicked
    // item. A gate counts once (its continuation cells are part of it); clicking a gate or a linked box
    // selects several CELLS but is still one pick, and keeps the ordinary inspector and menu.
    multiSelectedCount() {
        if (this.state.region.length === 0) {
            return 0;
        }
        const t = this.state.template;
        const n = this.selectionItems().filter((c) => {
            const cell = findCell(t, c.x, c.y);
            return cell !== undefined && !(cell.isGate && cell.gateLength === 0);
        }).length;
        return n > 1 ? n : 0;
    }
    // Replaces the selection with `region`, with the first item in it as the one the inspector shows.
    selectRegion(region) {
        const items = selectedItemCells(this.state.template, region);
        this.setState({ region, selected: items[0] ?? null, pending: null });
    }
    // ⇧-click: the item under (x, y) — all its cells — joins the selection, or leaves it when it is already
    // in. An empty cell toggles alone, so a lock's bare cells can be added one at a time.
    toggleInSelection(x, y) {
        const t = this.state.template;
        const current = this.selectionCells();
        const own = findCell(t, x, y) ? moveGroupCells(t, x, y) : [{ x, y }];
        const ownKeys = new Set(own.map((c) => cellKey(c.x, c.y)));
        const has = current.some((c) => ownKeys.has(cellKey(c.x, c.y)));
        const next = has ? current.filter((c) => !ownKeys.has(cellKey(c.x, c.y))) : [...current, ...own];
        const items = selectedItemCells(t, next);
        // The inspector follows the item just added; after a removal, the first one left.
        const lead = !has && findCell(t, x, y) ? { x, y } : (items[0] ?? null);
        this.setState({ region: next, selected: lead, pending: null });
    }
    moveSelectionBy(dx, dy) {
        const region = this.selectionCells();
        const r = moveSelection(this.state.template, region, dx, dy);
        if (!r) {
            return false;
        }
        const s = this.state.selected;
        this.setTemplate(r.template, s ? { x: s.x + dx, y: s.y + dy } : null, this.state.region.length > 0 ? r.region : []);
        return true;
    }
    deleteSelected() {
        const region = this.selectionCells();
        if (region.length === 0) {
            return;
        }
        const next = deleteSelection(this.state.template, region);
        if (next === this.state.template) {
            return;
        }
        // A deletion can empty the cell a pending link starts from; the link cannot finish from there.
        const p = this.state.pending;
        if (p && !findCell(next, p.x, p.y)) {
            this.setState({ pending: null });
        }
        this.setTemplate(next, null);
    }
    copySelected() {
        const clip = copySelection(this.state.template, this.selectionCells());
        if (!clip) {
            this.setState({ hint: "Nothing to copy — select something first." });
            return false;
        }
        this.clipboard = clip;
        const n = clip.items.length;
        this.setState({ hint: `Copied ${n} item${n === 1 ? "" : "s"}${clip.modifiers.length > 0 ? ` + ${clip.modifiers.length} modifier${clip.modifiers.length === 1 ? "" : "s"}` : ""} — ⌘V pastes where the pointer is.` });
        return true;
    }
    cutSelected() {
        if (this.copySelected()) {
            const hint = this.state.hint;
            this.deleteSelected();
            this.setState({ hint });
        }
    }
    // Pastes with the copy's top-left at `at` (the pointer, or the menu's cell). The paste becomes the
    // selection, so it can be dragged straight into place.
    pasteAt(at) {
        const clip = this.clipboard;
        if (!clip) {
            this.setState({ hint: "Nothing copied yet — ⌘C copies the selection." });
            return;
        }
        if (!at) {
            this.setState({ hint: "Point at the board where the paste should go, then ⌘V." });
            return;
        }
        const r = pasteClipboard(this.state.template, clip, at);
        if (!r) {
            this.setState({ hint: "No room to paste there — it would land on something." });
            return;
        }
        this.setTemplate(r.template, selectedItemCells(r.template, r.region)[0] ?? null, r.region);
    }
    // A copy of a multi-selection, set down beside it; the copy becomes the selection.
    duplicateRegion() {
        const r = duplicateSelection(this.state.template, this.selectionCells());
        if (!r) {
            this.setState({ hint: "Nothing duplicated — there is no room for a copy." });
            return;
        }
        this.setTemplate(r.template, selectedItemCells(r.template, r.region)[0] ?? null, r.region);
    }
    // Patches every selected cell that `applies` to, as ONE edit (one undo entry) — the keys that work on
    // "the selected box" work on all of them when several are selected.
    patchSelection(applies, patch) {
        let t = this.state.template;
        let any = false;
        for (const c of this.selectionItems()) {
            const cell = findCell(t, c.x, c.y);
            if (cell && applies(cell)) {
                any = true;
                const p = patch(cell);
                if (p)
                    t = withCellPatched(t, c.x, c.y, p);
            }
        }
        if (t !== this.state.template) {
            this.setTemplate(t, this.state.selected, this.state.region);
        }
        return any;
    }
    // A paint is an edit like any other — undoable, draft-saved, and it DOES invalidate the compiled
    // preview, because the colours are what the compile produces. What makes it different is that the
    // answer the author is asking for lives on the backend: the point of the click is to see the new
    // colour and the new verdict. So a paint schedules the recompile itself instead of leaving the board
    // colourless until Compile is pressed again.
    paint(next, selected = this.state.selected) {
        if (next === this.state.template) {
            // Nothing to repaint — the box already holds this colour, or the click landed on an empty cell.
            // The click still means "work on this one", and selecting is the only feedback that it
            // registered at all; returning outright made the most ordinary click in the tool do nothing.
            this.setState({ selected, region: [] });
            return;
        }
        this.setTemplate(next, selected);
        this.scheduleRecompile();
    }
    // Long enough that painting across several boxes compiles once at the end, short enough that a single
    // click feels immediate. A later paint replaces the pending one; compile() itself then drops any reply
    // that arrives for a template the board has already moved on from.
    //
    // It carries the SAME two guards as the Compile button and ⌘⏎, for the reasons written out at the ⌘⏎
    // branch: firing at a board the validator has already rejected earns a 400 whose failure path clears
    // the preview, to show an error the Issues panel is already showing; and overlapping compiles let a
    // slower earlier reply land last. Automatic firing makes both easier to hit than a keypress ever did.
    scheduleRecompile() {
        window.clearTimeout(this.recompileTimer);
        this.recompileTimer = window.setTimeout(() => {
            this.recompileTimer = undefined;
            if (this.state.compiling) {
                this.scheduleRecompile(); // one is in flight; come back for the board as it is by then
                return;
            }
            if (hasErrors(validateTemplate(this.state.template))) {
                return; // the Issues panel says why, and the panel keeps its last result marked stale
            }
            void this.compile();
        }, 180);
    }
    // Undo and redo drop the compiled preview like any other edit, so without this the board and the whole
    // Colours panel sit grey until Compile is pressed — the exact thing paint() schedules a recompile to
    // avoid, reintroduced by the inverse of a paint. Only once the author has compiled at least once:
    // before that the panel is closed and there is nothing to keep fresh.
    scheduleRecompileIfColoursAreShown() {
        if (this.state.lastColors) {
            this.scheduleRecompile();
        }
    }
    // The rows the menu shows for a cell: what is there decides what can be done to it.
    menuItems(x, y) {
        const occupied = findCell(this.state.template, x, y) !== undefined;
        // Right-clicking inside a selection of several items offers what can be done to all of them.
        const n = this.multiSelectedCount();
        if (n > 1 && this.selectionCells().some((c) => c.x === x && c.y === y)) {
            return [
                { id: "selDuplicate", label: `Duplicate ${n} items` },
                { id: "selCopy", label: "Copy" },
                { id: "selCut", label: "Cut" },
                { id: "selDelete", label: `Delete ${n} items` },
            ];
        }
        const items = occupied
            ? [
                { id: "delete", label: "Delete" },
                { id: "duplicate", label: "Duplicate" },
                { id: "duplicateN", label: "Duplicate ×", kind: "count" },
            ]
            : [
                { id: "box", label: "Add Box" },
                { id: "tunnel", label: "Add Tunnel" },
                { id: "superTunnel", label: "Add Super Tunnel" },
                { id: "toolBox", label: "Add Tool Box" },
                { id: "pipe", label: "Add Pipe" },
                { id: "propeller", label: "Add Propeller" },
                { id: "gate", label: "Add Gate" },
                { id: "cleared", label: "Add Cleared cell" },
            ];
        items.push({ id: "lock", label: `Add Lock ${LOCK_SIZE}×${LOCK_SIZE}` }, { id: "metalBox", label: "Add Metal Box 2×2" }, { id: "elevator", label: `Add Elevator ${ELEVATOR_SIZE}×${ELEVATOR_SIZE}` });
        // Covers go over a box — or over an empty cell, which gets a box under them. Never over a wall.
        if (singleCoverProblem(this.state.template, x, y) === null) {
            items.push({ id: "ice", label: "Add Ice" }, { id: "superMystery", label: "Add Super Mystery" });
        }
        // A multi-click tool in progress offers its NEXT point here; otherwise each one can be started here.
        const run = this.state.runPending;
        if (run) {
            const step = RUN_STEPS[run.tool][run.cells.length];
            items.push({ id: run.tool, label: `${RUN_NAMES[run.tool]}: ${step.toLowerCase()} here` });
        }
        else {
            items.push({ id: "door", label: "Start Sliding Door here" }, { id: "laser", label: "Start Laser Gate here" }, { id: "bolt", label: "Start Bolt here (head)" });
        }
        // Connect links two BOXES — the game's own rule, from CellSetGenerator.IsConnectable: "Connectors
        // only link two boxes, so tunnels and gate cells (both walls, not boxes) are excluded."
        //
        // Offered over an empty cell it started a link that could never be completed: the second click's
        // guard found nothing at the pending cell and dropped it without a word. Offered over a WALL it did
        // worse than nothing — a link's ends travel together when one is dragged, so a link onto a gate let
        // an ordinary box drag carry one cell of that gate away, leaving a head still claiming its full span
        // and an invisible stray continuation elsewhere. Only a Warning, so the board saved and published.
        if (isConnectable(this.state.template, x, y)) {
            items.push(this.state.pending ? { id: "connectTo", label: "Connect to here" } : { id: "connect", label: "Connect from here" });
        }
        if (this.clipboard) {
            items.push({ id: "paste", label: "Paste here" });
        }
        return items;
    }
    // Adds one point to the sliding door / laser gate / bolt being drawn; the last point lays it. A point that makes
    // the item impossible (off its row, too short, a key on a wall) is refused with a hint and the points
    // picked so far are kept, so one bad click does not throw away the others.
    addRunPoint(tool, x, y) {
        const t = this.state.template;
        const prev = this.state.runPending && this.state.runPending.tool === tool ? this.state.runPending.cells : [];
        const cells = [...prev, { x, y }];
        const type = tool === "door" ? CellModifierType.SlidingDoor : tool === "bolt" ? CellModifierType.Bolt : CellModifierType.LaserGate;
        if (cells.length === 2) {
            const problem = runProblem(t, type, cells[0], cells[1]);
            if (problem) {
                this.setState({ hint: problem });
                return;
            }
            if (tool === "door" || tool === "bolt") {
                this.setState({ runPending: null });
                const next = tool === "door" ? withSlidingDoor(t, cells[0], cells[1]) : withBolt(t, cells[0], cells[1]);
                this.setTemplate(next, { x: cells[0].x, y: cells[0].y });
                return;
            }
        }
        if (cells.length === 3) {
            const problem = laserKeyProblem(t, cells[0], cells[1], cells[2]);
            if (problem) {
                this.setState({ hint: problem });
                return;
            }
            this.setState({ runPending: null });
            this.setTemplate(withLaserGate(t, cells[0], cells[1], cells[2]), { x: cells[0].x, y: cells[0].y });
            return;
        }
        this.setState({ runPending: { tool, cells }, hint: null });
    }
    static isPaintable(m) {
        return LevelEditorPage.PAINTABLE.has(m);
    }
    // Places the active mechanic at (x, y) if it is empty, WITHOUT pushing an undo entry — the stroke
    // commits one entry when it ends (bypassing setTemplate, which would push one entry per painted cell).
    //
    // The UPDATER form of setState is load-bearing: the mousemove that begins a stroke calls paintStart and
    // paintOver back to back, and React does not update `this.state` mid-handler. Reading `this.state`
    // (as this did) made the second call start from the pre-stroke template and overwrite the first, so
    // every stroke silently dropped the cell it started on.
    paintAt(x, y) {
        const mechanic = this.state.mechanic;
        if (mechanic === "color") {
            // A colour stroke recolours the boxes it crosses. It shares this path (rather than calling
            // paint()) for the reason spelled out above: the updater form is what keeps the cell the stroke
            // STARTED on from being overwritten by the next cell in the same handler.
            const color = this.state.paintColor;
            this.setState((s) => {
                const next = withCellColor(s.template, x, y, color);
                return next === s.template ? null : { template: next, previewColors: null, compiled: null, balance: null, compileError: null };
            });
            this.scheduleRecompile();
            return;
        }
        if (!LevelEditorPage.isPaintable(mechanic)) {
            return;
        }
        this.setState((s) => {
            const next = withMechanicPainted(s.template, x, y, mechanic);
            return next === s.template ? null : { template: next, previewColors: null, compiled: null, balance: null, compileError: null };
        });
    }
    patchSelected(patch) {
        const s = this.state.selected;
        if (s) {
            this.setTemplate(withCellPatched(this.state.template, s.x, s.y, patch));
        }
    }
    // True when this keystroke is none of the editor's business: another tab is showing, or the author is
    // typing into a field.
    ignoresHotkeys() {
        if (!this.props.active) {
            return true;
        }
        const el = document.activeElement;
        if (!el) {
            return false;
        }
        const tag = el.tagName;
        return tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || (el instanceof HTMLElement && el.isContentEditable);
    }
    // The cell the author has selected, if any.
    selectedCell() {
        const s = this.state.selected;
        return s ? findCell(this.state.template, s.x, s.y) : undefined;
    }
    // Duplicates the item at (x, y) `count` times. One undo entry for the whole batch; a partial fill says
    // so in the toolbar rather than failing silently.
    duplicateAt(x, y, count) {
        const { template, placed } = duplicateCell(this.state.template, x, y, count);
        if (placed === 0) {
            this.setState({ hint: "Nothing duplicated — the board is full." });
            return;
        }
        // The hint is set AFTER the edit: setTemplate clears it, since a hint belongs to the edit that
        // raised it. Setting it first would put it up and then have setTemplate wipe it in the same batch.
        this.setTemplate(template);
        if (placed < count) {
            this.setState({ hint: `Duplicated ${placed} of ${count} — board full.` });
        }
    }
    duplicateSelected(count) {
        const s = this.state.selected;
        if (s) {
            this.duplicateAt(s.x, s.y, count);
        }
    }
    // The template with the gate under (x, y) removed whole, or the template itself when that cell belongs
    // to no gate — in which case the SAME reference comes back, so the no-op contract downstream survives.
    withoutGateUnder(t, x, y) {
        const cell = findCell(t, x, y);
        return cell && cell.isGate ? withGateClearedAt(t, x, y) : t;
    }
    // Applies a mechanic to (x, y). Mystery and Arrow are FLAGS on a box, so they are set without
    // disturbing each other (⇧2 then ⇧3 gives a mystery arrow); Box clears both and turns a tunnel/gate
    // back into a plain box. Lock and Connect are modifiers, not cell types: Lock drops its square with
    // (x, y) as the top-left corner, Connect starts the two-click link from here.
    applyMechanic(m, x, y) {
        const t = this.state.template;
        // A cell that belongs to a gate cannot be converted in place: withGate stamps the span's length onto
        // the head, so turning one of its cells into a box or a tunnel left the head claiming a span it no
        // longer owns. The validator only WARNS about that, so the broken board saved and published. Any
        // conversion takes the whole gate off first — the rule Delete already follows.
        const base = this.withoutGateUnder(t, x, y);
        switch (m) {
            case "select":
                // Places nothing: with Select held, a click or ⇧N just selects.
                this.setState({ selected: findCell(t, x, y) ? { x, y } : null, region: [] });
                return;
            case "color":
                // Not a thing you place: the Colour tool repaints what is already on the board, and every
                // click that carries it is handled before this method is reached (onLeftClick/onRightClick).
                // An empty cell has no colour to give, so a click that lands here means "nothing to paint".
                return;
            case "box":
                this.setTemplate(withCellAsPlainBox(base, x, y), { x, y });
                return;
            case "mystery":
                this.setTemplate(withBoxFlag(base, x, y, "isMystery"), { x, y });
                return;
            case "arrow":
                this.setTemplate(withBoxFlag(base, x, y, "isArrow"), { x, y });
                return;
            case "split":
                this.setTemplate(withCellAsSplitBox(base, x, y), { x, y });
                return;
            case "double":
                this.setTemplate(withCellAsDoubleBox(base, x, y), { x, y });
                return;
            case "jumpingJack":
                this.setTemplate(withCellAsJumpingJack(base, x, y), { x, y });
                return;
            case "tunnel":
                this.setTemplate(withCellAsTunnel(base, x, y), { x, y });
                return;
            case "superTunnel":
                this.setTemplate(withCellAsSuperTunnel(base, x, y), { x, y });
                return;
            case "ice":
            case "superMystery": {
                const problem = singleCoverProblem(t, x, y);
                if (problem) {
                    this.setState({ hint: problem, selected: { x, y }, region: [] });
                    return;
                }
                const next = withSingleCover(t, x, y, m === "ice" ? CellModifierType.FrozenBox : CellModifierType.SuperMysteryBox);
                if (next === t) {
                    this.setState({ selected: { x, y }, region: [] }); // already covered this way: the click still selects
                    return;
                }
                this.setTemplate(next, { x, y });
                return;
            }
            case "door":
            case "laser":
            case "bolt":
                this.addRunPoint(m, x, y);
                return;
            case "toolBox":
                this.setTemplate(withCellAsToolBox(base, x, y), { x, y });
                return;
            case "pipe":
                this.setTemplate(withCellAsPipe(base, x, y), { x, y });
                return;
            case "propeller":
                this.setTemplate(withCellAsPropeller(base, x, y), { x, y });
                return;
            case "metalBox": {
                // Placed over whatever is there, like the lock: the click is its top-left corner.
                const next = withMetalBox(t, x, y);
                if (next === t) {
                    this.setState({ selected: { x, y }, region: [] });
                    return;
                }
                this.setTemplate(next, { x, y });
                return;
            }
            case "elevator": {
                const next = withElevator(t, x, y);
                if (next === t) {
                    this.setState({ selected: { x, y }, region: [] });
                    return;
                }
                this.setTemplate(next, { x, y });
                return;
            }
            case "cleared":
                this.setTemplate(withCellAsCleared(base, x, y), { x, y });
                return;
            case "gate": {
                // withGateAt hands back the same template both when it refuses (no room) and when the gate
                // is already exactly there. Only the first of those is worth a word.
                const next = withGateAt(t, x, y);
                if (next === t && !gateFitsAt(t, x, y)) {
                    this.setState({ hint: `Gate needs ${GATE_LENGTH} free cells.` });
                    return;
                }
                this.setTemplate(next, { x, y });
                return;
            }
            case "lock":
                this.setTemplate(withLockPanel(t, x, y, DEFAULT_LOCK_COUNTER));
                return;
            case "connect":
                // Same rule the menu follows (isConnectable): a link needs a BOX at both ends. A pending
                // cell over an empty one is a dead end the second click discards silently; one over a gate
                // or a tunnel is a link the game has no meaning for and the drag would tear the wall with.
                if (!isConnectable(t, x, y)) {
                    this.setState({
                        hint: findCell(t, x, y)
                            ? "Connect links two boxes — a gate or a tunnel is a wall, not a box."
                            : "Connect links two boxes — start it on one.",
                    });
                    return;
                }
                this.setState({ pending: { x, y } });
                return;
        }
    }
    render() {
        const { template, previewColors, selected, mechanic, compiling } = this.state;
        // The repairs first: they explain what the file WAS, which is the context for anything the live
        // validation says about what it now is. Both lists share ValidationIssue so the panel and the
        // Save/Publish gate read them as one — a repair that ever needs to block Save only has to be
        // raised as an Error to do it.
        // What the ring means is "a colour here was chosen, not dealt" — so it has to follow where the
        // colour actually lives. A tunnel's colours live on its SPAWNS (a colour on the tunnel cell is
        // ignored, and the validator says so), and a gate holds no box at all, so a stray override there
        // marks nothing. Reading only the cell's own field left every hand-painted tunnel unringed while
        // AUTHORING.md promised the ring is how you tell which colours survive a new seed.
        const paintedCells = new Set(template.cells.items
            .filter((c) => {
            if (!c || c.isGate || c.isCleared) {
                return false;
            }
            const painted = (spawns) => (spawns ?? []).some((sp) => (sp?.colorOverride ?? MarbleColor.None) !== MarbleColor.None);
            return c.isTunnel
                ? painted(c.tunnelSpawns)
                : c.isSuperTunnel
                    ? painted(c.superTunnelSpawns)
                    : c.isToolBox
                        ? painted(c.toolBoxSpawns)
                        : c.isPipe
                            ? painted(c.pipeSpawns)
                            : c.isDouble
                                ? (c.colorOverride ?? MarbleColor.None) !== MarbleColor.None || (c.innerColorOverride ?? MarbleColor.None) !== MarbleColor.None
                                : c.isSplit
                                    ? Array.from({ length: SPLIT_COLUMNS }, (_, k) => splitColumnPaint(c, k)).some((p) => p !== MarbleColor.None)
                                    : (c.colorOverride ?? MarbleColor.None) !== MarbleColor.None;
        })
            .map((c) => `${c.x},${c.y}`));
        const previewSplits = previewColors && this.state.compiled ? splitColorsOf(this.state.compiled) : null;
        const previewInners = previewColors && this.state.compiled ? innerColorsOf(this.state.compiled) : null;
        const issues = [...this.state.loadIssues, ...validateTemplate(template)];
        const hasError = hasErrors(issues);
        const selection = new Set(this.selectionCells().map((c) => cellKey(c.x, c.y)));
        return (_jsxs("div", { className: "flex h-full flex-col", children: [this.renderToolbar(hasError), _jsxs("div", { className: "flex min-h-0 flex-1", children: [_jsxs("div", { className: "w-60 shrink-0 overflow-auto border-r border-slate-800 bg-slate-900 p-3", children: [this.renderTree(), this.renderIssues(issues), this.multiSelectedCount() > 1 ? this.renderSelection() : this.renderInspector(), this.renderLevelMeta(), this.renderGeneration(), this.renderCompiled()] }), _jsxs("div", { className: "flex flex-1 flex-col items-center gap-4 overflow-auto bg-slate-950/40 p-4", children: [this.renderPalette(), _jsx(GridCanvas, { template: template, selected: selected, selection: selection, marquee: this.state.marquee, selectMode: mechanic === "select", onCellHover: this.onCellHover, onMarqueeStart: this.onMarqueeStart, onMarqueeOver: this.onMarqueeOver, onMarqueeEnd: this.onMarqueeEnd, pending: this.state.pending, runPoints: this.runPoints(), previewColors: previewColors, previewSplits: previewSplits, previewInners: previewInners, paintedCells: paintedCells, onCellClick: this.onLeftClick, onCellRightClick: this.onRightClick, onCellDragStart: this.onCellDragStart, onCellDragOver: this.onCellDragOver, onCellDragEnd: this.onCellDragEnd, onCellPaintStart: this.onCellPaintStart, onCellPaintOver: this.onCellPaintOver, onCellPaintEnd: this.onCellPaintEnd, dragPaints: mechanic === "color" })] }), _jsx(ColorStudio, { template: template, compiled: this.state.lastColors?.level ?? null, balance: this.state.lastColors?.balance ?? null, stale: this.state.compiled === null, paintColor: this.state.paintColor, painting: mechanic === "color", busy: compiling, onPickColor: (color) => this.setState({ paintColor: color }), onPaintSlice: this.paintSlice, onFreezeSlice: this.freezeSlice, onThawAllSlices: this.thawAllSlices })] }), this.state.menu && (_jsx(CellContextMenu, { screenX: this.state.menu.screenX, screenY: this.state.menu.screenY, items: this.menuItems(this.state.menu.x, this.state.menu.y), count: this.state.dupCount, onCountChange: (n) => this.setState({ dupCount: n }), onSelect: this.onMenuSelect, onClose: () => this.setState({ menu: null }) }))] }));
    }
    // The multi-click tool's points so far, labelled for the board (see RUN_STEPS).
    runPoints() {
        const run = this.state.runPending;
        return run ? run.cells.map((p, i) => ({ ...p, label: RUN_STEPS[run.tool][i] })) : [];
    }
    // --- tree view of items + modifiers ---------------------------------------------------------------
    renderTree() {
        const { template, selected } = this.state;
        // A hand-edited file can hold a null entry in either array (see findCell) — and this is render(),
        // with no error boundary above it, so reading through one blanks the editor. `modifiers` is NOT
        // filtered: the rows below address a modifier by its index into the real array (withLockCounter,
        // and the × that rebuilds the list without it), so dropping entries here would shift those indices
        // and quietly write the nulls out of the file. Null rows are skipped inside the map instead.
        const items = template.cells.items.filter((c) => c != null);
        const mods = template.cells.modifiers;
        const inSelection = new Set(this.state.region.length > 0 ? this.selectionItems().map((c) => cellKey(c.x, c.y)) : []);
        const dirArrow = (d) => (d === GridDirection.Top ? "↑" : d === GridDirection.Bottom ? "↓" : d === GridDirection.Left ? "←" : "→");
        // The groups a tunnel's boxes carry, in spawn order — the same numbers the grid draws as pips over the
        // tunnel. Listed as text here rather than as pips: ten coloured circles would not fit this row beside
        // the coordinate and the label.
        const spawnGroups = (c) => Array.from({ length: Math.min(Math.max(0, c.tunnelSpawnCount), MAX_TUNNEL_SPAWNS) }, (_, i) => tunnelSpawnAt(c, i).group);
        const cellLabel = (c) => {
            if (c.isCleared) {
                return "Cleared";
            }
            if (c.isGate) {
                return c.gateLength > 0 ? `Gate ${c.gateLength}w ×${c.gateMultiplier}` : "Gate·cont";
            }
            if (c.isTunnel) {
                const groups = spawnGroups(c);
                return `Tunnel ${dirArrow(c.tunnelDirection)}×${c.tunnelSpawnCount}${groups.length > 0 ? ` g${groups.join(",")}` : ""}`;
            }
            if (c.isSuperTunnel) {
                const n = Math.min(Math.max(0, c.superTunnelSpawnCount ?? 0), MAX_SUPER_TUNNEL_SPAWNS);
                const groups = Array.from({ length: n }, (_, i) => superTunnelSpawnAt(c, i).group);
                return `Super tunnel ×${c.superTunnelSpawnCount ?? 0}${groups.length > 0 ? ` g${groups.join(",")}` : ""}`;
            }
            if (c.isToolBox) {
                const n = Math.min(Math.max(0, c.toolBoxSpawnCount ?? 0), MAX_TOOL_BOX_SPAWNS);
                const groups = Array.from({ length: n }, (_, i) => toolBoxSpawnAt(c, i).group);
                return `Tool box ×${c.toolBoxSpawnCount ?? 0}${groups.length > 0 ? ` g${groups.join(",")}` : ""}`;
            }
            if (c.isPropeller) {
                return `Propeller ${dirArrow(c.propellerDirection ?? GridDirection.Top)}${c.propellerHasBox ? " +box" : ""}`;
            }
            if (c.isPipe) {
                const n = Math.min(Math.max(0, c.pipeSpawnCount ?? 0), MAX_PIPE_SPAWNS);
                const groups = Array.from({ length: n }, (_, i) => pipeSpawnAt(c, i).group);
                return `Pipe ↓×${c.pipeSpawnCount ?? 0}${groups.length > 0 ? ` g${groups.join(",")}` : ""}`;
            }
            if (c.isDouble) {
                return "Double Box";
            }
            if (c.isJumpingJack) {
                return c.isMystery ? "Mystery Jumping Jack" : "Jumping Jack";
            }
            return `${c.isGlide ? "Glide " : ""}${c.isSplit ? "Split " : ""}${c.isMystery ? "Mystery" : "Box"}${c.isArrow ? ` ${dirArrow(c.arrowDirection)}` : ""}`;
        };
        return (_jsxs("div", { className: "mb-4", children: [_jsxs("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: ["Items (", items.length, ")"] }), _jsxs("ul", { className: "mt-1 max-h-44 space-y-0.5 overflow-auto", children: [items.length === 0 && _jsx("li", { className: "px-1.5 text-[11px] text-slate-600", children: "empty board" }), items.map((c) => {
                            const active = (selected && selected.x === c.x && selected.y === c.y) || inSelection.has(cellKey(c.x, c.y));
                            return (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => this.setState({ selected: { x: c.x, y: c.y }, region: [] }), className: "flex w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-left text-[11px] " + (active ? "bg-indigo-600/30 text-white" : "text-slate-300 hover:bg-slate-800"), children: [!isWall(c) && (_jsx("span", { className: "inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold text-white", style: { background: `hsl(${(c.group * 67) % 360} 70% 45%)` }, children: c.group })), _jsxs("span", { className: "font-mono text-slate-500", children: [c.x, ",", c.y] }), _jsx("span", { className: "truncate", children: cellLabel(c) })] }) }, `${c.x},${c.y}`));
                        })] }), _jsxs("h3", { className: "mt-3 text-xs font-semibold uppercase tracking-wide text-slate-400", children: ["Modifiers (", mods.length, ")"] }), _jsxs("ul", { className: "mt-1 space-y-0.5", children: [mods.length === 0 && _jsx("li", { className: "px-1.5 text-[11px] text-slate-600", children: "none" }), mods.map((m, i) => {
                            // `cells` arrives off Drive with no schema check, so a hand-edited modifier can be
                            // missing it entirely; render() has no error boundary above it, so a throw here
                            // blanks the whole editor. Skip the row rather than guess at a position to show.
                            const a = m && Array.isArray(m.cells) ? m.cells[0] : undefined;
                            if (!a) {
                                return null;
                            }
                            const label = modifierLabel(m);
                            return (_jsxs("li", { className: "flex items-center gap-1", children: [_jsx("button", { type: "button", onClick: () => this.setState({ selected: { x: a.x, y: a.y }, region: [] }), className: "flex-1 truncate rounded px-1.5 py-0.5 text-left text-[11px] text-slate-300 hover:bg-slate-800", children: label }), m.type === CellModifierType.LockPanel && (_jsx(NumberInput, { value: m.counter ?? 0, min: 0, onChange: (v) => this.setTemplate(withLockCounter(template, i, v)), ariaLabel: `Unlock after N slices for lock ${i}`, className: "w-9 rounded border border-slate-700 bg-slate-800 px-1 py-0.5 text-[11px] text-slate-100 focus:border-indigo-500 focus:outline-none" })), _jsx("button", { type: "button", title: "Remove modifier", onClick: () => this.setTemplate(withModifierRemoved(template, i)), className: "rounded px-1 text-[11px] text-slate-500 hover:bg-red-600/60 hover:text-white", children: "\u00D7" })] }, i));
                        })] })] }));
    }
    // --- validation issues panel ----------------------------------------------------------------------
    renderIssues(issues) {
        if (issues.length === 0) {
            return _jsx("div", { className: "mb-4 text-xs text-emerald-400", children: "\u2713 No issues" });
        }
        return (_jsxs("div", { className: "mb-4", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: "Issues" }), _jsx("ul", { className: "mt-2 space-y-1", children: issues.map((iss, i) => {
                        const isErr = iss.severity === ValidationSeverity.Error;
                        const clickable = iss.x >= 0 && iss.y >= 0;
                        return (_jsx("li", { children: _jsxs("button", { type: "button", disabled: !clickable, onClick: () => clickable && this.setState({ selected: { x: iss.x, y: iss.y }, region: [] }), className: "w-full rounded-md border px-2 py-1 text-left text-[11px] " +
                                    (isErr ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300") +
                                    (clickable ? " hover:brightness-125" : " cursor-default"), children: [isErr ? "⛔ " : "⚠ ", iss.message] }) }, i));
                    }) })] }));
    }
    // --- toolbar (mechanics + stats + actions) ---------------------------------------------------------
    // --- mechanic palette (above the board) -----------------------------------------------------------------
    // One tile per tool: the item drawn with the board's own art, its name under it, its ⇧ key in the corner.
    renderPalette() {
        const { mechanic } = this.state;
        const byId = new Map(MECHANICS.map((m) => [m.id, m]));
        const grouped = new Set(PALETTE_GROUPS.flatMap((g) => g.ids));
        const other = MECHANICS.filter((m) => !grouped.has(m.id)).map((m) => m.id);
        const groups = other.length > 0 ? [...PALETTE_GROUPS, { label: "Other", ids: other }] : PALETTE_GROUPS;
        return (_jsx("div", { className: "flex max-w-full flex-wrap items-stretch justify-center gap-x-0 gap-y-3 rounded-2xl border border-slate-800/80 bg-gradient-to-b from-slate-900/95 to-slate-950/95 px-3 py-2.5 shadow-2xl shadow-black/40 ring-1 ring-white/5", children: groups.map((g, gi) => (_jsxs("div", { className: "flex flex-col gap-1.5 px-2.5" + (gi > 0 ? " border-l border-slate-800" : ""), children: [_jsx("span", { className: "px-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500", children: g.label }), _jsx("div", { className: "flex gap-1", children: g.ids.map((id) => {
                            const m = byId.get(id);
                            if (!m)
                                return null;
                            const active = mechanic === m.id;
                            return (_jsxs("button", { type: "button", onClick: () => this.setState({ mechanic: m.id, pending: null, runPending: null }), title: `${m.key ? `${m.label} (⇧${m.key})` : m.badge ? `${m.label} (${m.badge})` : m.label}${m.help ? ` — ${m.help}` : ""}`, className: "group relative flex h-[80px] w-[68px] flex-col items-center justify-between rounded-xl border px-1 pb-1.5 pt-2.5 transition duration-150 ease-out " +
                                    (active
                                        ? "-translate-y-0.5 border-indigo-400/90 bg-gradient-to-b from-indigo-500/45 to-indigo-950/80 text-white shadow-lg shadow-indigo-500/40 ring-2 ring-indigo-400/60"
                                        : "border-slate-700/60 bg-gradient-to-b from-slate-800/90 to-slate-900 text-slate-300 shadow-md shadow-black/30 hover:-translate-y-0.5 hover:border-indigo-400/50 hover:text-white hover:shadow-indigo-500/20"), children: [(m.key || m.badge) && (_jsx("span", { className: "absolute right-1 top-1 z-10 rounded px-1 font-mono text-[9px] leading-4 " +
                                            (active ? "bg-indigo-300/25 text-indigo-100" : "bg-black/35 text-slate-400 group-hover:text-slate-200"), children: m.key ? `⇧${m.key}` : m.badge })), _jsx("span", { className: "transition duration-150 ease-out group-hover:scale-110" + (active ? " scale-110 drop-shadow-[0_0_10px_rgba(129,140,248,0.55)]" : ""), children: _jsx(MechanicIcon, { id: m.id, width: 60, height: 44 }) }), _jsx("span", { className: "w-full text-center text-[10px] font-semibold leading-tight", children: m.label })] }, m.id));
                        }) })] }, g.label))) }));
    }
    renderToolbar(hasError) {
        const { mechanic, template, compiling, compiled, unityReady, exportingWalls, compileError, previewColors, saving, saveStatus, hint } = this.state;
        const stats = templateStats(template);
        const activeLabel = MECHANICS.find((m) => m.id === mechanic)?.label ?? mechanic;
        return (_jsxs("div", { className: "border-b border-slate-800 px-4 py-2.5", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs("div", { className: "flex gap-1", children: [_jsx("button", { type: "button", onClick: this.undo, disabled: this.state.past.length === 0, title: "Undo (\u2318Z)", className: "rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 disabled:opacity-40", children: "\u21B6" }), _jsx("button", { type: "button", onClick: this.redo, disabled: this.state.future.length === 0, title: "Redo (\u2318\u21E7Z)", className: "rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 disabled:opacity-40", children: "\u21B7" }), _jsx("button", { type: "button", onClick: this.clearBoard, title: "Clear the board", className: "rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700", children: "Clear" })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsxs("label", { className: "flex items-center gap-1.5 text-[11px] text-slate-400", children: ["Name", _jsx("input", { type: "text", value: template.name ?? "", 
                                            // Through setMetadata like every other edit — a direct setState here wrote the
                                            // name behind undo's back (⌘Z after a rename silently reverted it along with the
                                            // board change before it) and behind the draft timer's (a rename-only edit never
                                            // reached storage, so a refresh recovered a draft with the old name).
                                            onChange: (e) => this.setMetadata(withName(this.state.template, e.target.value)), className: "w-28 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 font-mono text-xs text-slate-100 focus:border-indigo-500 focus:outline-none" })] }), this.renderNavButton(-1), this.renderNavButton(1)] }), this.state.navArmed !== null && (_jsxs("span", { className: "text-[11px] text-red-300", children: ["Unsaved changes \u2014 press ", this.state.navArmed > 0 ? "›" : "‹", " again to drop them and open", " ", levelDisplayName(this.navNeighbour(this.state.navArmed)?.name ?? (this.state.navArmed > 0 ? "the next level" : "the previous level"))] })), _jsxs("div", { className: "ml-auto flex items-center gap-2", children: [_jsx("button", { type: "button", onClick: this.compile, disabled: compiling || hasError, title: hasError ? "Fix the errors first" : "Compile + preview colours (⌘⏎ compiles and plays)", className: "rounded-md bg-slate-700 px-3 py-1 text-xs font-semibold text-white transition hover:bg-slate-600 disabled:opacity-50", children: compiling ? "Compiling…" : "Compile" }), _jsx("button", { type: "button", onClick: this.play, disabled: !compiled || !unityReady, title: unityReady ? "Send the compiled level to the game" : "Waiting for the game to load…", className: "rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50", children: "Play" }), _jsx("button", { type: "button", onClick: this.exportWalls, disabled: !unityReady || exportingWalls, title: unityReady
                                        ? `Download the walls of the level the game is PLAYING as one transparent ${NATIVE_EXPORT_WIDTH}x${NATIVE_EXPORT_HEIGHT} PNG (every island, positioned as the camera sees them)`
                                        : "Waiting for the game to load…", className: "rounded-md bg-slate-700 px-3 py-1 text-xs font-semibold text-white transition hover:bg-slate-600 disabled:opacity-50", children: exportingWalls ? "Exporting…" : "Export walls" }), _jsx(TargetChip, { onPick: this.props.onPickEnvironment }), _jsx("button", { type: "button", onClick: this.save, disabled: saving || hasError, title: hasError ? "Fix the errors first" : "Save this template into the environment shown to the left of this button", className: "rounded-md bg-slate-700 px-3 py-1 text-xs font-semibold text-white transition hover:bg-slate-600 disabled:opacity-50", children: "Save" }), _jsx("button", { type: "button", onClick: this.publish, disabled: saving || hasError, title: hasError ? "Fix the errors first" : "Compile and store the level the game plays (appears under Levels → Game Levels) in the environment shown to the left", className: "rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white transition hover:bg-indigo-500 disabled:opacity-50", children: saving ? "…" : "Publish" })] })] }), _jsxs("div", { className: "mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400", children: [_jsxs("span", { children: [stats.boxes, " boxes"] }), _jsxs("span", { children: [stats.mystery, " mystery"] }), _jsxs("span", { children: [stats.tunnels, " tunnels"] }), _jsxs("span", { children: [template.cells.modifiers.length, " modifiers"] }), _jsxs("span", { children: [stats.gates, " gates"] }), stats.cleared > 0 && _jsxs("span", { children: [stats.cleared, " cleared"] }), _jsxs("span", { children: [stats.groups, " groups"] }), _jsxs("span", { children: [template.columns, "\u00D7", template.rows] }), _jsxs("span", { className: "text-slate-500", children: [mechanic === "select"
                                    ? "left-click: select an item · drag: box-select · drag inside the selection: move it · ⇧click: add / remove"
                                    : mechanic === "connect"
                                        ? "left-click: select an item · right-click an item: menu → “Connect from here”"
                                        : mechanic === "door"
                                            ? "left-click the ANCHOR (the housing the door retracts into), then the far end — on one row or column · esc cancels"
                                            : mechanic === "laser"
                                                ? "left-click one end, the other end (same row or column), then the KEY box that opens it · esc cancels"
                                                : mechanic === "bolt"
                                                    ? "left-click the HEAD, then the far end (same row or column, 3 boxes) — only the box just beyond the head unlocks it · esc cancels"
                                                    : mechanic === "metalBox"
                                                        ? "left-click the top-left cell of a 2×2 to lay a metal box over it · right-click: menu"
                                                        : mechanic === "elevator"
                                                            ? "left-click the top-left cell of a 2×2 to put an elevator under it · right-click: menu"
                                                            : mechanic === "ice" || mechanic === "superMystery"
                                                                ? `left-click a box: cover it with ${activeLabel} (an empty cell gets a box too) · right-click: menu`
                                                                : mechanic === "color"
                                                                    // Every clause of the generic line is false here: a click paints instead of
                                                                    // selecting, right-click clears the paint instead of opening the menu, and a
                                                                    // drag repaints what it crosses instead of moving it.
                                                                    ? `left-click: paint ${COLOR_NAMES[this.state.paintColor] ?? "Auto"} · right-click: back to the seed · drag: paint what you cross`
                                                                    : `left-click: place ${activeLabel} / select an item · right-click: menu · drag an item: move`, LevelEditorPage.isPaintable(mechanic) ? ` · drag from an empty cell: paint ${activeLabel}` : ""] }), _jsx("span", { className: "text-slate-600", children: "keys: v select \u00B7 \u21E7drag box-select \u00B7 \u2318A all \u00B7 \u21E7\u2190\u2191\u2192\u2193 move \u00B7 \u2318C/\u2318X/\u2318V copy/cut/paste \u00B7 del delete \u00B7 m mystery \u00B7 g glide \u00B7 s split \u00B7 b double \u00B7 j jumping jack \u00B7 d duplicate \u00B7 0\u20139 group \u00B7 \u2190\u2191\u2192\u2193 aim \u00B7 \u2318Z/\u2318\u21E7Z undo \u00B7 \u2318\u23CE compile+play \u00B7 esc deselect" }), this.state.pending && _jsx("span", { className: "text-amber-400", children: "right-click the second cell \u2192 \u201CConnect to here\u201D\u2026" }), this.state.runPending && (_jsxs("span", { className: "text-amber-400", children: [RUN_NAMES[this.state.runPending.tool], ": now pick the", " ", RUN_STEPS[this.state.runPending.tool][this.state.runPending.cells.length].toLowerCase(), "\u2026"] })), compileError && _jsx("span", { className: "text-red-400", children: compileError }), previewColors && !compileError && _jsx("span", { className: "text-emerald-400", children: "compiled \u2713 (showing colours)" }), saveStatus && _jsx("span", { className: "text-sky-400", children: saveStatus }), hint && _jsx("span", { className: "text-amber-400", children: hint })] }), this.state.draftOffer && (_jsxs("div", { className: "mt-2 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300", children: [_jsxs("span", { children: ["Unsaved draft of \u201C", this.state.draftOffer.template.name || "untitled", "\u201D", this.state.draftOffer.savedAt > 0 ? ` — ${new Date(this.state.draftOffer.savedAt).toLocaleString()}` : ""] }), _jsx("button", { type: "button", onClick: this.restoreDraft, className: "rounded bg-amber-500/20 px-2 py-0.5 hover:bg-amber-500/30", children: "Restore" }), _jsx("button", { type: "button", onClick: this.discardDraft, title: "Throws the draft away \u2014 this cannot be undone", className: "rounded px-2 py-0.5 " +
                                (this.state.draftDiscardArmed
                                    ? "bg-red-600/70 text-white hover:bg-red-600"
                                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"), children: this.state.draftDiscardArmed ? "Discard for good?" : "Discard" })] }))] }));
    }
    // --- selection panel (several items selected) ------------------------------------------------------
    // Takes the inspector's place while more than one item is selected: what the selection holds, and the
    // things that can be done to all of it.
    renderSelection() {
        const t = this.state.template;
        const cells = this.selectionItems()
            .map((c) => findCell(t, c.x, c.y))
            .filter((c) => c !== undefined && !(c.isGate && c.gateLength === 0));
        const count = (f) => cells.filter(f).length;
        const parts = [
            [count((c) => !c.isGate && !c.isTunnel && !c.isCleared), "boxes"],
            [count((c) => c.isTunnel), "tunnels"],
            [count((c) => c.isGate), "gates"],
            [count((c) => c.isCleared === true), "cleared"],
        ];
        const btn = "rounded-md bg-slate-800 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-700";
        return (_jsxs("div", { className: "mb-4", children: [_jsxs("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: ["Selection (", cells.length, " items)"] }), _jsx("p", { className: "mt-1 text-[11px] text-slate-400", children: parts
                        .filter(([n]) => n > 0)
                        .map(([n, label]) => `${n} ${label}`)
                        .join(" · ") }), _jsxs("div", { className: "mt-2 grid grid-cols-2 gap-1", children: [_jsx("button", { type: "button", className: btn, onClick: () => this.duplicateRegion(), title: "A copy beside the selection (d)", children: "Duplicate" }), _jsx("button", { type: "button", className: btn, onClick: () => this.copySelected(), title: "\u2318C \u2014 \u2318V pastes where the pointer is", children: "Copy" }), _jsx("button", { type: "button", className: btn, onClick: () => this.cutSelected(), title: "\u2318X", children: "Cut" }), _jsx("button", { type: "button", className: "rounded-md bg-red-600/30 px-2 py-1 text-[11px] text-red-200 hover:bg-red-600/60", onClick: () => this.deleteSelected(), title: "Delete / Backspace", children: "Delete" })] }), _jsxs("div", { className: "mt-2 flex items-center gap-1", children: [_jsx("span", { className: "text-[11px] text-slate-500", children: "Move" }), [["←", -1, 0], ["↑", 0, -1], ["↓", 0, 1], ["→", 1, 0]].map(([label, dx, dy]) => (_jsx("button", { type: "button", className: "rounded bg-slate-800 px-2 py-0.5 text-[11px] text-slate-200 hover:bg-slate-700", title: `⇧${label}`, onClick: () => {
                                if (!this.moveSelectionBy(dx, dy)) {
                                    this.setState({ hint: "Can't move that way — something is in the way, or it's the edge." });
                                }
                            }, children: label }, label)))] }), _jsx("p", { className: "mt-2 text-[10px] leading-snug text-slate-500", children: "Drag any selected item to move them all \u00B7 0\u20139 sets the group of every box \u00B7 m mystery \u00B7 \u2190\u2191\u2192\u2193 aims tunnels and arrows \u00B7 a lock or other area moves only when the selection covers all of it" })] }));
    }
    // --- inspector (selected cell) --------------------------------------------------------------------
    renderInspector() {
        const { selected, template } = this.state;
        const cell = selected ? findCell(template, selected.x, selected.y) : undefined;
        const lockIndex = selected ? lockIndexAt(template, selected.x, selected.y) : -1;
        if (!cell) {
            return (_jsxs("div", { className: "mb-4", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: "Cell" }), _jsx("p", { className: "mt-1 text-xs text-slate-500", children: "Select a cell to edit its group, order, and mystery flag." }), lockIndex >= 0 && this.renderLockPanel(lockIndex), selected && this.renderSeals(selected.x, selected.y)] }));
        }
        const isGateHead = cell.isGate && cell.gateLength > 0;
        const isGateCont = cell.isGate && cell.gateLength === 0;
        const isCleared = cell.isCleared === true;
        const typeLabel = isCleared ? "Cleared" : cell.isGate ? (isGateHead ? "Gate" : "Gate (cont.)") : cell.isTunnel ? "Tunnel" : cell.isSuperTunnel ? "Super tunnel" : cell.isToolBox ? "Tool box" : cell.isPipe ? "Pipe" : cell.isPropeller ? "Propeller" : "Box";
        const { x, y } = cell;
        return (_jsxs("div", { className: "mb-4", children: [_jsxs("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: [typeLabel, " (", cell.x, ", ", cell.y, ")"] }), _jsxs("div", { className: "mt-2 space-y-2", children: [isGateCont && _jsx("p", { className: "text-xs text-slate-500", children: "Continuation of a gate \u2014 edit the gate from its left-end cell." }), isCleared && (_jsx("p", { className: "text-xs text-slate-500", children: "Board ground with nothing on it, as if a box had stood here and dropped, socket and all. The game draws no wall over it and builds nothing on it, and the reveal runs through it from the start. \u21E71 turns it back into a box." })), isGateHead && (_jsxs(_Fragment, { children: [this.numberField("Length (cells)", cell.gateLength, (v) => this.setTemplate(withGate(template, x, y, Math.max(1, Math.min(v, gateMaxLengthAt(template, x, y))), cell.gateMultiplier), { x, y }), 1, gateMaxLengthAt(template, x, y), false, "blur"), this.numberField("Multiplier (×balls)", cell.gateMultiplier, (v) => this.setTemplate(withGate(template, x, y, cell.gateLength, Math.max(1, v)), { x, y }), 1, undefined, false, "blur"), _jsxs("p", { className: "text-[11px] text-slate-500", children: ["Every box above the gate drops \u00D7", cell.gateMultiplier, " balls through it. The conveyor's slices are derived from the multiplied counts, so the level stays balanced by itself."] })] })), cell.isTunnel && (_jsxs(_Fragment, { children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Direction" }), _jsx("select", { value: cell.tunnelDirection, onChange: (e) => this.patchSelected({ tunnelDirection: parseInt(e.target.value, 10) }), className: "w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none", children: DIRECTIONS.map((d) => (_jsx("option", { value: d.v, children: d.label }, d.v))) })] }), this.numberField("Spawn count", cell.tunnelSpawnCount, (v) => this.setTemplate(withTunnelSpawnCount(template, x, y, Math.max(0, v)), { x, y }), 0, MAX_TUNNEL_SPAWNS, false, "blur"), cell.tunnelSpawnCount > MAX_TUNNEL_SPAWNS && (
                                // A file can carry a count no editor would author (the ops clamp what this
                                // one writes). One row per spawn is an unbounded render driven by that
                                // number, and it runs the moment the cell is selected — a big one froze the
                                // tab before the ⛔ beside it could be read. Draw the rows that fit, say what
                                // is being held back, and let the Issues panel do the explaining.
                                _jsxs("p", { className: "text-[11px] text-amber-400", children: ["Showing the first ", MAX_TUNNEL_SPAWNS, " of ", cell.tunnelSpawnCount, " spawns \u2014 see Issues."] })), this.renderSpawnRows(Math.min(cell.tunnelSpawnCount, MAX_TUNNEL_SPAWNS), (i) => tunnelSpawnAt(cell, i), (i, patch) => this.setTemplate(withTunnelSpawn(template, x, y, i, patch), { x, y }), (i, color) => this.paint(withTunnelSpawnColor(template, x, y, i, color), { x, y }))] })), cell.isSuperTunnel && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[11px] text-slate-500", children: "Serves all four neighbours: when the box beside it is dropped, it turns to that cell and pushes out its next box. An empty cell beside it stays empty." }), this.numberField("Boxes it holds", cell.superTunnelSpawnCount ?? 0, (v) => this.setTemplate(withSuperTunnelSpawnCount(template, x, y, Math.max(0, v)), { x, y }), 0, MAX_SUPER_TUNNEL_SPAWNS, false, "blur"), (cell.superTunnelSpawnCount ?? 0) > MAX_SUPER_TUNNEL_SPAWNS && (_jsxs("p", { className: "text-[11px] text-amber-400", children: ["Showing the first ", MAX_SUPER_TUNNEL_SPAWNS, " of ", cell.superTunnelSpawnCount, " spawns \u2014 see Issues."] })), this.renderSpawnRows(Math.min(cell.superTunnelSpawnCount ?? 0, MAX_SUPER_TUNNEL_SPAWNS), (i) => superTunnelSpawnAt(cell, i), (i, patch) => this.setTemplate(withSuperTunnelSpawn(template, x, y, i, patch), { x, y }), (i, color) => this.paint(withSuperTunnelSpawnColor(template, x, y, i, color), { x, y }))] })), cell.isToolBox && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[11px] text-slate-500", children: "Throws its whole load at once into the closest free cells, as soon as that many are free, then leaves. It wakes up only when a box beside it is dropped." }), this.numberField("Boxes it throws", cell.toolBoxSpawnCount ?? 0, (v) => this.setTemplate(withSpawnerCount(template, x, y, "toolBox", Math.max(0, v)), { x, y }), 0, MAX_TOOL_BOX_SPAWNS, false, "blur"), (cell.toolBoxSpawnCount ?? 0) > MAX_TOOL_BOX_SPAWNS && (_jsxs("p", { className: "text-[11px] text-amber-400", children: ["Showing the first ", MAX_TOOL_BOX_SPAWNS, " of ", cell.toolBoxSpawnCount, " spawns \u2014 see Issues."] })), this.renderSpawnRows(Math.min(cell.toolBoxSpawnCount ?? 0, MAX_TOOL_BOX_SPAWNS), (i) => toolBoxSpawnAt(cell, i), (i, patch) => this.setTemplate(withSpawnerSpawn(template, x, y, "toolBox", i, patch), { x, y }), (i, color) => this.paint(withToolBoxSpawnColor(template, x, y, i, color), { x, y }))] })), cell.isPipe && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[11px] text-slate-500", children: "A tunnel that always feeds the cell straight below it. Its body runs up the column above it into the board's top wall, so keep that column empty." }), this.numberField("Boxes it holds", cell.pipeSpawnCount ?? 0, (v) => this.setTemplate(withSpawnerCount(template, x, y, "pipe", Math.max(0, v)), { x, y }), 0, MAX_PIPE_SPAWNS, false, "blur"), (cell.pipeSpawnCount ?? 0) > MAX_PIPE_SPAWNS && (_jsxs("p", { className: "text-[11px] text-amber-400", children: ["Showing the first ", MAX_PIPE_SPAWNS, " of ", cell.pipeSpawnCount, " spawns \u2014 see Issues."] })), this.renderSpawnRows(Math.min(cell.pipeSpawnCount ?? 0, MAX_PIPE_SPAWNS), (i) => pipeSpawnAt(cell, i), (i, patch) => this.setTemplate(withSpawnerSpawn(template, x, y, "pipe", i, patch), { x, y }), (i, color) => this.paint(withPipeSpawnColor(template, x, y, i, color), { x, y }))] })), cell.isPropeller && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[11px] text-slate-500", children: "Tied to the box beside it. When that box drops, the propeller takes off, flies to a random box on the board and blows it up \u2014 that box's screws go straight to the slices, like the magnet. Its own cell then opens up like a dropped box's \u2014 or, with a box underneath, that box appears there and opens if the reveal reaches it." }), _jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Tied to the box" }), _jsx("select", { value: cell.propellerDirection ?? GridDirection.Top, onChange: (e) => this.setTemplate(withPropellerDirection(template, x, y, parseInt(e.target.value, 10)), { x, y }), className: "w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none", children: DIRECTIONS.map((d) => (_jsx("option", { value: d.v, children: d.label }, d.v))) })] }), _jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", title: "A plain box hidden under the propeller: it appears once the propeller has flown", children: [_jsx("input", { type: "checkbox", checked: cell.propellerHasBox ?? false, onChange: (e) => this.setTemplate(withPropellerBox(template, x, y, e.target.checked), { x, y }) }), "Box underneath"] }), (cell.propellerHasBox ?? false) && (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsxs("span", { className: "mb-1 block text-[11px] text-slate-400", children: ["Box colour \u2014 ", COLOR_NAMES[paintedCellColor(template, x, y)] ?? "Auto"] }), this.renderColorRow(paintedCellColor(template, x, y), (color) => this.paint(withCellColor(template, x, y, color), { x, y }))] }), this.numberField("Group", cell.group, (v) => this.patchSelected({ group: Math.max(0, v) }), 0), this.numberField("Sibling", cell.sibling, (v) => this.patchSelected({ sibling: Math.max(0, v) }), 0), this.numberField("Sort order", cell.sortOrder, (v) => this.patchSelected({ sortOrder: Math.max(0, v) }), 0)] }))] })), !isWall(cell) && (_jsxs(_Fragment, { children: [cell.isSplit ? (
                                // A split box is painted column by column: one colour cannot paint three.
                                SPLIT_COLUMN_NAMES.map((name, k) => (_jsxs("div", { children: [_jsxs("span", { className: "mb-1 block text-[11px] text-slate-400", children: [name, " column \u2014 ", COLOR_NAMES[splitColumnPaint(cell, k)] ?? "Auto"] }), this.renderColorRow(splitColumnPaint(cell, k), (color) => this.paint(withSplitColumnColor(template, x, y, k, color), { x, y }))] }, name)))) : (_jsxs("div", { children: [_jsxs("span", { className: "mb-1 block text-[11px] text-slate-400", children: [cell.isDouble ? "Double box colour" : "Colour", " \u2014 ", COLOR_NAMES[paintedCellColor(template, x, y)] ?? "Auto"] }), this.renderColorRow(paintedCellColor(template, x, y), (color) => this.paint(withCellColor(template, x, y, color), { x, y }))] })), cell.isDouble && (
                                // The closed box standing inside it, which plays first.
                                _jsxs("div", { children: [_jsxs("span", { className: "mb-1 block text-[11px] text-slate-400", children: ["Inside colour \u2014 ", COLOR_NAMES[cell.innerColorOverride ?? MarbleColor.None] ?? "Auto"] }), this.renderColorRow(cell.innerColorOverride ?? MarbleColor.None, (color) => this.paint(withInnerColor(template, x, y, color), { x, y }))] })), this.numberField("Group", cell.group, (v) => this.patchSelected({ group: Math.max(0, v) }), 0), this.numberField("Sibling", cell.sibling, (v) => this.patchSelected({ sibling: Math.max(0, v) }), 0), this.numberField("Sort order", cell.sortOrder, (v) => this.patchSelected({ sortOrder: Math.max(0, v) }), 0)] })), !isWall(cell) && (_jsxs(_Fragment, { children: [_jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", children: [_jsx("input", { type: "checkbox", checked: cell.isMystery, onChange: (e) => this.patchSelected({ isMystery: e.target.checked, ...leavingDouble(cell, e.target.checked) }) }), "Mystery box"] }), _jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", title: "Its screws float out and land on the funnel instead of dropping (g)", children: [_jsx("input", { type: "checkbox", checked: cell.isGlide ?? false, onChange: (e) => this.patchSelected({ isGlide: e.target.checked, ...leavingDouble(cell, e.target.checked), ...leavingJack(cell, e.target.checked) }) }), "Glide box"] }), _jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", title: "Three colours, one per column: three screws of each (s)", children: [_jsx("input", { type: "checkbox", checked: cell.isSplit ?? false, onChange: (e) => this.setTemplate(withSplit(template, x, y, e.target.checked), { x, y }) }), "Split box"] }), _jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", title: "A closed box of another colour stands inside it and plays first; then its own screws (b)", children: [_jsx("input", { type: "checkbox", checked: cell.isDouble ?? false, onChange: (e) => this.setTemplate(withDouble(template, x, y, e.target.checked), { x, y }) }), "Double box"] }), _jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", title: "Its spring launches the screws up high; a box it opens can reach the throat first (j)", children: [_jsx("input", { type: "checkbox", checked: cell.isJumpingJack ?? false, onChange: (e) => this.setTemplate(withJumpingJack(template, x, y, e.target.checked), { x, y }) }), "Jumping jack"] }), _jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", children: [_jsx("input", { type: "checkbox", checked: cell.isArrow, onChange: (e) => this.patchSelected({ isArrow: e.target.checked, ...leavingDouble(cell, e.target.checked), ...leavingJack(cell, e.target.checked) }) }), "Arrow"] }), cell.isArrow && (_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Arrow direction" }), _jsx("select", { value: cell.arrowDirection, onChange: (e) => this.patchSelected({ arrowDirection: parseInt(e.target.value, 10) }), className: "w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none", children: DIRECTIONS.map((d) => (_jsx("option", { value: d.v, children: d.label }, d.v))) })] }))] }))] }), lockIndex >= 0 && this.renderLockPanel(lockIndex), this.renderSeals(x, y)] }));
    }
    // A spawner's per-spawn rows — a tunnel's or a super tunnel's: each spawn is its own box, so each gets its
    // own group / sibling / sort and its own colour. `count` is already bounded by the caller.
    renderSpawnRows(count, spawnAt, onPatch, onPaint) {
        if (count <= 0) {
            return null;
        }
        return (_jsxs("div", { children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Spawns (each its own group / sibling / sort)" }), _jsx("div", { className: "space-y-1.5", children: Array.from({ length: count }, (_, i) => {
                        const s = spawnAt(i);
                        return (_jsxs("div", { className: "rounded-md border border-slate-700 bg-slate-800/40 p-1.5", children: [_jsxs("div", { className: "mb-1 flex items-center gap-1.5 text-[11px] text-slate-400", children: [_jsx("span", { className: "inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold text-white", style: { background: `hsl(${(s.group * 67) % 360} 70% 45%)` }, children: s.group }), "spawn ", i] }), _jsxs("div", { className: "grid grid-cols-3 gap-1", children: [this.miniField("Grp", s.group, (v) => onPatch(i, { group: v })), this.miniField("Sib", s.sibling, (v) => onPatch(i, { sibling: v })), this.miniField("Sort", s.sortOrder, (v) => onPatch(i, { sortOrder: v }))] }), this.renderColorRow(s.colorOverride ?? MarbleColor.None, (color) => onPaint(i, color))] }, i));
                    }) })] }));
    }
    // The game-mechanics seals over (x, y) — ice, a super mystery box, a sliding door, a laser gate (the cell
    // may be its KEY) — each with its own fields and a way to remove it. The lock panel keeps its own block.
    renderSeals(x, y) {
        const { template } = this.state;
        const indices = sealIndicesAt(template, x, y).filter((i) => {
            const type = template.cells.modifiers[i].type;
            return type !== CellModifierType.LockPanel;
        });
        if (indices.length === 0) {
            return null;
        }
        const remove = (i, label) => (_jsxs("button", { type: "button", onClick: () => this.setTemplate(withModifierRemoved(template, i)), className: "mt-2 w-full rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-red-600/60 hover:text-white", children: ["Remove ", label] }));
        return indices.map((i) => {
            const m = template.cells.modifiers[i];
            const a = m.cells[0];
            const b = m.cells[1] ?? a;
            let body = null;
            switch (m.type) {
                case CellModifierType.MetalBox:
                    body = (_jsxs(_Fragment, { children: [this.numberField("Hits to break (3 fresh)", m.counter, (v) => this.setTemplate(withMetalHits(template, i, v)), 1, METAL_BOX_HITS), _jsx("p", { className: "mt-1 text-[11px] text-slate-500", children: "Hides the boxes under it. Each box dropped beside the 2\u00D72 is one hit: a chain snaps, then the other, then the plate comes off." }), remove(i, "metal box")] }));
                    break;
                case CellModifierType.Bolt:
                    body = (_jsxs(_Fragment, { children: [_jsxs("p", { className: "text-[11px] text-slate-500", children: ["Head (", a.x, ",", a.y, ") \u2192 far end (", b.x, ",", b.y, "). The boxes stay in sight but locked; only dropping the box just beyond the head unlocks it (marked UNLOCK on the board). The game's art is made for ", BOLT_LENGTH, " boxes."] }), _jsx("button", { type: "button", onClick: () => this.setTemplate(withRunFlipped(template, i)), className: "mt-2 w-full rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700", children: "Swap head and far end" }), remove(i, "bolt")] }));
                    break;
                case CellModifierType.FrozenBox:
                    body = (_jsxs(_Fragment, { children: [this.numberField("Hits to break (2 fresh, 1 already cracked)", m.counter, (v) => this.setTemplate(withIceHits(template, i, v)), 1, FROZEN_BOX_HITS), _jsx("p", { className: "mt-1 text-[11px] text-slate-500", children: "The box stays in sight but closed. Each box dropped beside it is one hit; the last hit shatters the ice." }), remove(i, "ice")] }));
                    break;
                case CellModifierType.SuperMysteryBox:
                    body = (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[11px] text-slate-500", children: "Hides the whole box. The first box dropped beside it half-opens the cover; the player then TAPS it to lift it and drop the box underneath." }), remove(i, "super mystery box")] }));
                    break;
                case CellModifierType.SlidingDoor: {
                    const full = runLength(a, b);
                    body = (_jsxs(_Fragment, { children: [_jsxs("p", { className: "text-[11px] text-slate-500", children: ["Anchor (", a.x, ",", a.y, ") \u2192 far end (", b.x, ",", b.y, "). Each box dropped beside it retracts it one cell toward the anchor; at ", SLIDING_DOOR_MIN_LENGTH, " cells the next hit removes it."] }), this.numberField("Starts this many cells long", m.counter, (v) => this.setTemplate(withDoorLength(template, i, v)), Math.min(SLIDING_DOOR_MIN_LENGTH, full), full), _jsx("button", { type: "button", onClick: () => this.setTemplate(withRunFlipped(template, i)), className: "mt-2 w-full rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700", children: "Swap anchor and far end" }), remove(i, "sliding door")] }));
                    break;
                }
                case CellModifierType.Elevator:
                    body = (_jsxs(_Fragment, { children: [_jsxs("p", { className: "text-[11px] text-slate-500", children: ["Stands under the boxes on its ", ELEVATOR_SIZE, "\u00D7", ELEVATOR_SIZE, ". Once all four have dropped it opens and raises the four boxes listed below, top-left to bottom-right."] }), this.renderSpawnRows(ELEVATOR_SIZE * ELEVATOR_SIZE, (s) => elevatorSpawnAt(m, s), (s, patch) => this.setTemplate(withElevatorSpawn(template, i, s, patch)), (s, color) => this.paint(withElevatorSpawnColor(template, i, s, color))), remove(i, "elevator")] }));
                    break;
                case CellModifierType.LaserGate: {
                    const k = m.cells[2];
                    const isKey = k !== undefined && k.x === x && k.y === y;
                    body = (_jsxs(_Fragment, { children: [_jsxs("p", { className: "text-[11px] text-slate-500", children: [isKey ? "This box is the KEY: dropping it opens the gate " : "Beams ", "(", a.x, ",", a.y, ")\u2013(", b.x, ",", b.y, ")", k ? `, key box (${k.x},${k.y})` : ", no key box", ". Every box on the run stays sealed until the key box is dropped."] }), _jsxs("label", { className: "mt-1.5 block", children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Pair color" }), _jsx("select", { value: m.pairColor ?? 0, onChange: (e) => this.setTemplate(withLaserPairColor(template, i, parseInt(e.target.value, 10))), className: "w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none", children: LASER_PAIR_COLORS.map((c) => (_jsx("option", { value: c.v, children: c.label }, c.v))) })] }), remove(i, "laser gate")] }));
                    break;
                }
                default:
                    return null;
            }
            return (_jsxs("div", { className: "mt-3 rounded-md border border-slate-700 bg-slate-800/40 p-2", children: [_jsx("h4", { className: "mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400", children: modifierLabel(m) }), body] }, i));
        });
    }
    // The lock panel covering the selected cell. A lock is a modifier, not a cell, so it gets its own
    // block: the same counter the Modifiers list shows, plus the way to delete it without hunting for the
    // row up there.
    renderLockPanel(index) {
        const { template } = this.state;
        const m = template.cells.modifiers[index];
        // `cells` arrives off Drive with no schema check, so a hand-edited modifier can be missing it
        // entirely. `lockIndexAt` already treats that as "covers nothing" and won't hand back this index
        // for it, but guard directly too rather than lean on that indirection to avoid a throw in render().
        const a = m && Array.isArray(m.cells) ? m.cells[0] : undefined;
        if (!a) {
            return null;
        }
        const b = m.cells[1] || a;
        return (_jsxs("div", { className: "mt-3 rounded-md border border-slate-700 bg-slate-800/40 p-2", children: [_jsxs("h4", { className: "text-[11px] font-semibold uppercase tracking-wide text-slate-400", children: ["Lock panel (", a.x, ",", a.y, ")\u2013(", b.x, ",", b.y, ")"] }), _jsxs("label", { className: "mt-1.5 block", children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Unlock after (slices cleared)" }), _jsx(NumberInput, { value: m.counter ?? 0, min: 0, onChange: (v) => this.setTemplate(withLockCounter(template, index, v)), ariaLabel: "Unlock after N slices cleared", className: "w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none" })] }), _jsx("button", { type: "button", onClick: () => this.setTemplate({ ...template, cells: { ...template.cells, modifiers: template.cells.modifiers.filter((_, j) => j !== index) } }), className: "mt-2 w-full rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-red-600/60 hover:text-white", children: "Remove lock" })] }));
    }
    // --- authoring metadata (not compiled into LevelData) ----------------------------------------------
    renderLevelMeta() {
        const { template } = this.state;
        return (_jsxs("div", { className: "mb-3", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: "Level" }), _jsxs("label", { className: "mt-2 block", children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Difficulty" }), _jsxs("select", { value: template.difficulty ?? LevelDifficulty.Normal, onChange: (e) => this.setMetadata(withDifficulty(template, parseInt(e.target.value, 10))), className: "w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none", children: [_jsx("option", { value: LevelDifficulty.Normal, children: "Normal" }), _jsx("option", { value: LevelDifficulty.Hard, children: "Hard" }), _jsx("option", { value: LevelDifficulty.VeryHard, children: "Very hard" })] })] })] }));
    }
    // --- generation params + board size ---------------------------------------------------------------
    renderGeneration() {
        const { template } = this.state;
        const g = template.generation;
        return (_jsxs("div", { children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: "Generation" }), _jsxs("div", { className: "mt-2 space-y-2", children: [this.numberField("Palette size", g.paletteSize, (v) => this.setTemplate(withGeneration(template, { paletteSize: Math.min(MAX_PALETTE, Math.max(1, v)) })), 1, MAX_PALETTE), _jsxs("div", { className: "flex items-center gap-2", children: [this.numberField("Seed", g.seed, (v) => this.setTemplate(withGeneration(template, { seed: v })), undefined, undefined, true), _jsx("button", { type: "button", onClick: () => this.setTemplate(withGeneration(template, { seed: Math.floor(Math.random() * 1000000000) })), className: "mt-4 rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700", title: "Randomize seed", children: "\uD83C\uDFB2" })] }), _jsxs("label", { className: "flex items-center gap-2 text-xs text-slate-300", children: [_jsx("input", { type: "checkbox", checked: g.evenDistribution, onChange: (e) => this.setTemplate(withGeneration(template, { evenDistribution: e.target.checked })) }), "Even distribution"] }), _jsxs("div", { children: [_jsxs("div", { className: "mb-1 flex items-center justify-between", children: [_jsx("span", { className: "text-[11px] text-slate-400", children: "Mystery slice chance % (per wedge)" }), _jsx("button", { type: "button", onClick: this.setMysteryChanceAll, className: "rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-700", title: "Copy wedge 0's value to every wedge", children: "all =" })] }), _jsx("p", { className: "mb-1 text-[10px] leading-snug text-slate-500", children: "Odds each slice in that wedge stays hidden until it becomes active. The first slice of a pile is never hidden." }), _jsx("div", { className: "flex gap-1.5", children: Array.from({ length: WEDGE_COUNT }, (_, i) => (_jsxs("label", { className: "flex-1", title: `Wedge ${i}`, children: [_jsx("span", { className: "mb-0.5 block text-center text-[10px] text-slate-500", children: i }), _jsx("input", { type: "text", inputMode: "decimal", "aria-label": `Mystery slice chance for wedge ${i}, percent`, value: this.state.mysteryDrafts[i] ?? String(this.mysteryPercentAt(i)), onFocus: (e) => e.currentTarget.select(), onMouseDown: this.mysteryPercentSelectGuards[i].onMouseDown, onMouseUp: this.mysteryPercentSelectGuards[i].onMouseUp, onKeyDown: (e) => {
                                                    if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
                                                        e.preventDefault();
                                                        e.currentTarget.select();
                                                    }
                                                }, onChange: (e) => this.onMysteryPercentInput(i, e.target.value), onBlur: () => this.clearMysteryDraft(i), className: "w-full rounded border border-slate-700 bg-slate-800 px-1 py-0.5 text-xs text-slate-100 focus:border-indigo-500 focus:outline-none" })] }, i))) })] }), _jsxs("div", { children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: "Guaranteed colours (optional)" }), _jsx("div", { className: "flex flex-wrap gap-1.5", children: Array.from({ length: Math.max(0, Math.min(MAX_PALETTE, Number(g.paletteSize) || 0)) }, (_, i) => {
                                        // randColorList is indexed by PALETTE SLOT — BuildColorPool's stage 0 adds
                                        // `usable[i]`, the colour sitting in slot i. So the chip has to be read out of
                                        // the SELECTED preset's order (paletteStore), the same list the backend deals
                                        // from. It used to pass `i + 1` straight to colorHexOf, which is the enum
                                        // VALUE, and that agreed with the slot only while the two lists shared an order.
                                        // Reordering the palette would have left the designer setting one colour and
                                        // seeing another, with nothing to report it — the data was never wrong, only
                                        // the swatch. Naming the colour in the tooltip is the other half of that fix.
                                        const color = paletteStore.colors[i] ?? MarbleColor.None;
                                        const label = `Guaranteed copies of ${COLOR_NAMES[color] ?? `palette slot ${i + 1}`} (slot ${i + 1})`;
                                        return (_jsxs("label", { className: "flex items-center gap-1", title: label, children: [_jsx("span", { className: "inline-block h-3 w-3 rounded", style: { background: colorHexOf(color) } }), _jsx(NumberInput, { value: (g.randColorList && g.randColorList[i]) || 0, min: 0, onChange: (v) => this.setRandColor(i, v), ariaLabel: label, className: "w-11 rounded border border-slate-700 bg-slate-800 px-1 py-0.5 text-xs text-slate-100 focus:border-indigo-500 focus:outline-none" })] }, i));
                                    }) })] })] })] }));
    }
    // Wedge i's authored rate (0 when the array is shorter/absent — same rule the compiler uses).
    mysteryChanceAt(index) {
        return this.state.template.generation.mysterySliceChances?.[index] ?? 0;
    }
    // The boxes show a PERCENT of the 0..1 stored rate. Displayed exactly when the rate is a whole percent
    // (the usual case, and what the source data is); otherwise trimmed, because a 32-bit rate read back as
    // a double is 33.500000834… and nobody wants to see that. A corrupt file can carry a non-finite rate,
    // which the ⛔ next to it explains far better than a box reading "Infinity" — show 0 and let the issue
    // do the talking (typing over it recovers, and Save/Publish stay blocked meanwhile).
    mysteryPercentAt(index) {
        const p = this.mysteryChanceAt(index) * 100;
        if (!Number.isFinite(p)) {
            return 0;
        }
        const whole = Math.round(p);
        return Math.abs(p - whole) < 1e-4 ? whole : Number(p.toFixed(4));
    }
    // Typing is committed on every keystroke that parses, but the BOX keeps showing what was typed. Without
    // that split the field could not pass through "33." or "" on the way to "33.5": React rewrote the old
    // text and the next digit joined a different number, so 33.5 landed on 100 and backspace could not empty
    // the box. Unparseable/empty text simply doesn't commit; blur drops the draft and the stored value shows.
    onMysteryPercentInput(index, raw) {
        this.setState((s) => ({ mysteryDrafts: s.mysteryDrafts.map((d, i) => (i === index ? raw : d)) }));
        // A comma reads as the decimal point: `inputMode="decimal"` shows a locale keypad, and on most of
        // Europe that key emits ",". Without this "33,5" parsed as far as "33" and silently dropped the
        // rest. There is no thousands separator to confuse it with — the box tops out at 100.
        const v = Number(raw.replace(",", "."));
        if (raw.trim() !== "" && Number.isFinite(v)) {
            this.setMysteryChance(index, Math.min(1, Math.max(0, v / 100)));
        }
    }
    clearMysteryDraft(index) {
        this.setState((s) => ({ mysteryDrafts: s.mysteryDrafts.map((d, i) => (i === index ? null : d)) }));
    }
    // Writes wedge `index`, padding the array to WEDGE_COUNT so every box round-trips explicitly.
    setMysteryChance(index, chance) {
        const arr = Array.from({ length: WEDGE_COUNT }, (_, i) => this.mysteryChanceAt(i));
        if (arr[index] === chance) {
            return; // no-op: don't burn an undo entry or drop the compiled preview
        }
        arr[index] = chance;
        this.setTemplate(withGeneration(this.state.template, { mysterySliceChances: arr }));
    }
    setRandColor(index, value) {
        const g = this.state.template.generation;
        const arr = g.randColorList ? [...g.randColorList] : [];
        // Padded to the palette size, but never past MAX_PALETTE: `paletteSize` is read off the TEMPLATE and
        // a file can carry any number at all, so an unbounded loop here would hang the tab on the first
        // keystroke in one of these boxes — the same hole the row of boxes itself had. A palette that big is
        // already an Error in the Issues panel; this just refuses to freeze before it can be read.
        while (arr.length < Math.min(MAX_PALETTE, Number(g.paletteSize) || 0)) {
            arr.push(0);
        }
        const next = Math.max(0, value);
        // withGeneration compares its two ARRAY fields by identity, so a freshly built list always reads as
        // a change and this method could never reach that guard — the check has to happen here, the way
        // setMysteryChance already does it. These boxes commit on every keystroke, so without it re-typing
        // the number already showing burned an entry off the 50-deep undo stack and dropped the compiled
        // preview (Play with it) for a change of nothing. Padding counts as a real change: it writes zeros
        // the file did not carry.
        if (arr[index] === next && arr.length === (g.randColorList?.length ?? 0)) {
            return;
        }
        arr[index] = next;
        this.setTemplate(withGeneration(this.state.template, { randColorList: arr }));
    }
    // --- compiled preview: per-colour slice counts + copyable JSON ------------------------------------
    renderCompiled() {
        const { compiled, showJson } = this.state;
        if (!compiled) {
            return null;
        }
        const slicesByColor = new Map();
        for (const s of compiled.slices.stacks) {
            for (const it of s.items) {
                slicesByColor.set(it.color, (slicesByColor.get(it.color) || 0) + 1);
            }
        }
        const entries = [...slicesByColor.entries()].filter(([c]) => c !== MarbleColor.None).sort((a, b) => a[0] - b[0]);
        const json = JSON.stringify(compiled, null, 2);
        return (_jsxs("div", { className: "mt-4", children: [_jsx("h3", { className: "text-xs font-semibold uppercase tracking-wide text-slate-400", children: "Compiled" }), _jsx("div", { className: "mt-2 grid grid-cols-2 gap-1", children: entries.map(([c, n]) => (_jsxs("div", { className: "flex items-center gap-1.5 text-[11px] text-slate-300", children: [_jsx("span", { className: "inline-block h-3 w-3 rounded", style: { background: colorHexOf(c) } }), n, " slices"] }, c))) }), _jsxs("div", { className: "mt-2 flex gap-2", children: [_jsx("button", { type: "button", onClick: () => navigator.clipboard?.writeText(json), className: "rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700", children: "Copy JSON" }), _jsxs("button", { type: "button", onClick: () => this.setState({ showJson: !showJson }), className: "rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700", children: [showJson ? "Hide" : "Show", " JSON"] })] }), showJson && (_jsx("textarea", { readOnly: true, value: json, className: "mt-2 h-40 w-full rounded-md border border-slate-700 bg-slate-950 p-2 font-mono text-[10px] text-slate-300" }))] }));
    }
    // The swatch row used wherever ONE thing is painted from the inspector: a box, or one of a tunnel's
    // spawns. "Auto" clears the override and hands that box back to the seed — which is why it is a button
    // here and not the absence of one; there has to be a way back.
    renderColorRow(current, onPick) {
        return (_jsxs("div", { className: "grid grid-cols-7 gap-0.5", children: [_jsx("button", { type: "button", title: "Auto \u2014 leave this colour to the seed", "aria-label": "Auto (leave this colour to the seed)", "aria-pressed": current === MarbleColor.None, onClick: () => onPick(MarbleColor.None), className: `flex h-5 items-center justify-center rounded border text-[8px] ${current === MarbleColor.None
                        ? "border-indigo-400 bg-slate-700 text-slate-100"
                        : "border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700"}`, children: "auto" }), PAINTABLE_COLORS.map((c) => (_jsx("button", { type: "button", title: COLOR_NAMES[c], "aria-label": COLOR_NAMES[c], "aria-pressed": current === c, onClick: () => onPick(c), className: `h-5 rounded border ${current === c ? "border-indigo-400 ring-1 ring-indigo-400" : "border-slate-700 hover:border-slate-500"}`, style: { background: colorHexOf(c) } }, c)))] }));
    }
    // A very compact labelled number input for tight grids (e.g. per-spawn group/sibling/sort).
    miniField(label, value, onChange) {
        return (_jsxs("label", { className: "block", children: [_jsx("span", { className: "mb-0.5 block text-[9px] uppercase tracking-wide text-slate-500", children: label }), _jsx(NumberInput, { value: value, min: 0, onChange: onChange, ariaLabel: label, className: "w-full rounded border border-slate-700 bg-slate-800 px-1.5 py-0.5 text-xs text-slate-100 focus:border-indigo-500 focus:outline-none" })] }));
    }
    // A compact labelled number input (grow = fill available width). `commitOn` rides through to
    // NumberInput for the one field where a half-typed number would destroy data.
    numberField(label, value, onChange, min, max, grow = false, commitOn) {
        return (_jsxs("label", { className: "block " + (grow ? "flex-1" : ""), children: [_jsx("span", { className: "mb-1 block text-[11px] text-slate-400", children: label }), _jsx(NumberInput, { value: value, min: min, max: max, onChange: onChange, commitOn: commitOn, ariaLabel: label, className: "w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none" })] }));
    }
}
// --- paint -----------------------------------------------------------------------------------------
// A left-drag that starts on an empty cell lays down the active mechanic in every empty cell it
// crosses. Multi-cell mechanics (gate) and modifiers (lock/connect) are not paintable — one stroke
// would produce a pile of overlapping panels.
LevelEditorPage.PAINTABLE = new Set(PAINTABLE_MECHANICS);
// --- keyboard ------------------------------------------------------------------------------------
// Arrow keys aim the selected tunnel/arrow. Everything else the editor binds is a single letter or
// digit, so the guard below matters more than the bindings: a keystroke meant for a text field must
// never reach these.
LevelEditorPage.ARROW_KEYS = {
    ArrowUp: GridDirection.Top,
    ArrowDown: GridDirection.Bottom,
    ArrowLeft: GridDirection.Left,
    ArrowRight: GridDirection.Right,
};
